/**
 * Web store API – talks to Stylon backend (http://localhost:3000/v1).
 * Use REACT_APP_API_URL in .env to override base URL.
 */
import API from '../Config/Api';
import { applyNameAsTypeAxis } from '../utils/variantOptions';
import { getVisitorKey } from '../utils/visitorKey';
import { rememberShopCampaigns } from '../utils/shopCampaignsCache';
import { expectShop, initMetaPixel } from '../utils/metaPixel';
import { metaPixelCookieHeaders } from '../utils/metaPixelCookies';
import { maybeRecordAdLanding } from '../utils/adLanding';
import { maybeRecordSocialLanding } from '../utils/socialLanding';
import { maybeRecordBroadcastClick } from '../utils/broadcastLanding';
import { rememberCurrentShop } from '../utils/currentShop';

/**
 * True when the user ACTIVELY picked a language via the switcher (vs. the
 * device/auto default written by the index.js bootstrap). The switchers set
 * localStorage 'languageExplicit' = '1'; the device bootstrap never does.
 * The backend uses this to let a salon's "force language" override an
 * auto-default but never a customer's real choice.
 */
export function isLanguageExplicit() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('languageExplicit') === '1';
  } catch (_) {
    return false;
  }
}

/** Mark the current language as an explicit, user-initiated choice. */
export function markLanguageExplicit() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem('languageExplicit', '1');
  } catch (_) { /* ignore (private mode) */ }
}

// —— Auth (public) ——
// Origin is captured so the SMS OTP message can include a matching
// WebOTP marker (`@<host> #<code>`) — Chrome on Android refuses to
// auto-fill unless the marker host equals the page's origin host.
export async function sendOtp(phone, role = 'CUSTOMER', locale, channel) {
  const body = { phone, role };
  if (locale && ['en', 'ar', 'he'].includes(locale)) body.locale = locale;
  // 'whatsapp' lets the user force WhatsApp from the OTP screen when the SMS
  // never arrives (no cellular signal). Omitted → server smart-routes.
  if (channel === 'sms' || channel === 'whatsapp') body.channel = channel;
  if (typeof window !== 'undefined' && window.location?.origin) {
    body.origin = window.location.origin;
  }
  const { data } = await API.post('/auth/send-otp', body);
  return data;
}

export async function verifyOtp(phone, otp, opts = {}) {
  const body = { phone, otp };
  // Persist the customer's locale at account creation. `localeExplicit`
  // distinguishes an active choice from a device/auto default so a salon's
  // "force language" can override the latter but never the former.
  if (opts.locale && ['en', 'ar', 'he'].includes(opts.locale)) {
    body.locale = opts.locale;
    body.localeExplicit = !!opts.localeExplicit;
  }
  const { data } = await API.post('/auth/verify-otp', body);
  return data;
}

export async function onboardCustomer(body) {
  try {
    const { data } = await API.post('/auth/onboard/customer', body);
    return data;
  } catch (err) {
    // ONE DEPLOY BEHIND. Sign-up now names its business (`salonId`) so the
    // server can apply that business's gender question. The storefront goes
    // live minutes before the server; a server that does not know `salonId`
    // yet refuses the whole body ("property salonId should not exist"). Sign
    // the customer up without it rather than take no sign-ups at all — the old
    // server asked nothing of gender anyway. Nothing else retries: the same
    // rule `createOrder` follows for `expectedTotal`.
    const messages = [].concat(err?.response?.data?.message ?? []);
    const unknownSalonId = err?.response?.status === 400
      && body && 'salonId' in body
      && messages.some((m) => /property salonId should not exist/.test(String(m)));
    if (!unknownSalonId) throw err;
    const { salonId, ...withoutIt } = body;
    const { data } = await API.post('/auth/onboard/customer', withoutIt);
    return data;
  }
}

// —— Auth (with token) ——
export async function getMe() {
  const { data } = await API.get('/auth/me');
  return data;
}

/**
 * The towns a customer may pick from, for their own profile.
 *
 * `/cities`, not `/salons/:id/cities` — the salon-scoped routes are behind
 * `SalonMemberGuard`, which a customer passes for nobody. This one serves the
 * SHARED catalogue only: a town a single business added belongs to that
 * business and is not on offer here.
 *
 * `country` is required and is a two-letter code. It is not defaulted, because
 * guessing one of the three would hand a customer in the other two the wrong
 * country's towns with nothing on screen to say so.
 *
 * `lang` is, because the route orders what it returns by the name the reader
 * sees and falls back to English. Silence here means an Arabic reader gets the
 * shared catalogue in English alphabetical order, which is no order at all.
 */
export async function searchCities(q, country, limit) {
  const { data } = await API.get('/cities', {
    params: { q: q || undefined, country, limit, lang: readingLanguage() },
  });
  return Array.isArray(data) ? data : [];
}

/** The two-letter code this browser is reading in. English if it will not say. */
function readingLanguage() {
  try {
    return String(localStorage.getItem('language') || 'en').split('-')[0] || 'en';
  } catch (e) {
    // A crawler render and a locked-down private window both throw here.
    return 'en';
  }
}

/** Update current customer profile (fullName, dateOfBirth, gender, photoUrl, cityKey, addressLine). Returns updated user + context. */
export async function updateCustomerProfile(payload) {
  const { data } = await API.patch('/auth/me', payload);
  return data;
}

/** Upload customer profile photo (multipart). Returns { url } to use in updateCustomerProfile({ photoUrl }). */
export async function uploadCustomerPhoto(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await API.post('/upload/customer-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * Update current user's preferred language/locale (used for notifications).
 * `explicit` (default true) marks an active, user-initiated choice — a salon's
 * "force language" can override a device/auto default but never an explicit one.
 * Pass `false` for sync calls that only carry a device default.
 */
export async function updateUserLocale(locale, explicit = true) {
  const { data } = await API.patch('/auth/me/locale', { locale, explicit });
  return data;
}

// —— Public salon (store page by slug). When customerId is passed (logged-in), campaigns include remainingUses for "you used it all". ——
/*
 * EVERY SHOP PAGE STARTS THE SHOP'S PIXEL AND RECORDS THE AD THAT BROUGHT THE SHOPPER.
 *
 * Every storefront page loads its business through getSalonBySlug or getSalonByHost — the
 * home page's action, and the product, shop, collection, brand and checkout pages directly.
 * Only the action used to start the Pixel and record the ad landing, so a shopper sent by a
 * catalogue ad straight to a product page sent Meta no product events and no click was
 * recorded. Doing it here covers every page, including ones added later.
 */
function noteShopVisit(key, data) {
  const salon = data?.salon ?? data;
  if (!salon) return;
  // The slug too: on a business's own domain the shop is loaded by host, and its events name it.
  // …and whether the shop hands its shoppers' steps to the server as well (utils/shopperSteps.js).
  initMetaPixel(salon.metaPixelId, { currency: salon.currency, shop: key, slug: salon.slug ?? salon.username, serverEvents: salon.metaServerEvents === true });
  // …with its slug, so the steps that follow the click are told apart from another business's pages.
  maybeRecordAdLanding(salon.id ?? salon._id, salon.slug ?? salon.username ?? null);
  // …or the Social post whose link brought them (ref=social&sp=…): one click per visit, never blocking.
  maybeRecordSocialLanding(salon.id ?? salon._id);
  // …and the WhatsApp broadcast whose button brought them.
  maybeRecordBroadcastClick();
  // …and which shop the tab is on, for the floating help button that lives outside the pages (DK parity, S1d).
  rememberCurrentShop(salon);
}

export async function getSalonBySlug(slug, customerId) {
  expectShop(slug);
  const url = `/config/salon-by-slug/${encodeURIComponent(slug)}`;
  const params = customerId ? { customerId } : {};
  const { data } = await API.get(url, { params });
  // The cart drawer prices its lines with these; leaving them here spares it a
  // second salon request on every page that has just made this one.
  rememberShopCampaigns(slug, (data?.salon ?? data)?.campaigns);
  noteShopVisit(slug, data);
  return data;
}

// —— Public salon (store page by custom domain host). Custom-domain mode
// resolves the salon by hostname instead of by slug. ——
export async function getSalonByHost(host, customerId) {
  expectShop(host);
  const url = `/config/salon-by-host/${encodeURIComponent(host)}`;
  const params = customerId ? { customerId } : {};
  const { data } = await API.get(url, { params });
  noteShopVisit(host, data);
  return data;
}

/** Apply campaigns to cart (pricing). Public. */
export async function applyCampaigns(slug, dto) {
  const url = `/config/salon-by-slug/${encodeURIComponent(slug)}/apply-campaigns`;
  if (typeof window !== "undefined") {
    console.log("[BookingCampaign] applyCampaigns request", { slug, url, dto });
  }
  try {
    const { data } = await API.post(url, dto);
    if (typeof window !== "undefined") {
      console.log("[BookingCampaign] applyCampaigns response", {
        subtotal: data?.subtotal,
        discountTotal: data?.discountTotal,
        total: data?.total,
        appliedCampaignsCount: data?.appliedCampaigns?.length ?? 0,
        appliedNames: data?.appliedCampaigns?.map((c) => c.name) ?? [],
      });
    }
    return data;
  } catch (err) {
    if (typeof window !== "undefined") {
      console.warn("[BookingCampaign] applyCampaigns error", err?.response?.status, err?.response?.data ?? err?.message);
    }
    throw err;
  }
}

/**
 * WHAT A SHOP BASKET WILL BE CHARGED, from the server — the cart drawer's total.
 *
 * The same public `apply-campaigns` route and the same body the checkout's
 * quote and the order use (variant ids and quantities, today's date; never a
 * price), so the drawer's number is the checkout's number. Quiet, unlike
 * `applyCampaigns` above, because it runs every time a basket changes; and it
 * takes an AbortSignal, so a basket changed again mid-flight cancels the
 * question it no longer needs answered.
 */
export async function quoteProductCart(slug, productItems, { signal } = {}) {
  const url = `/config/salon-by-slug/${encodeURIComponent(slug)}/apply-campaigns`;
  const { data } = await API.post(
    url,
    { productItems, bookingDate: new Date().toISOString() },
    signal ? { signal } : undefined,
  );
  return data;
}

/** Record campaign click for analytics. Public, no auth. */
export async function recordCampaignClick(salonId, campaignId, source, visitorKey) {
  await API.post(`/salons/${salonId}/campaigns/${campaignId}/click`, { source, visitorKey });
}

/**
 * Record an AD landing (visitor arrived from a Google/Meta ad) so conversions can be
 * attributed + uploaded back to the platform for smart bidding. `platformClickId` is the
 * gclid/fbclid the platform appended. Public, fire-and-forget (202).
 */
/* ── ONE WINDOW OF CARDS IS ONE REQUEST ────────────────────────────────────
 *
 * This posted immediately, from its own `setTimeout(0)`. One event, one POST —
 * fine for the product page, wrong for a grid that reports a `product_view`
 * per rendered card. Measured on dk-store at 1440: 24 POSTs on first paint,
 * 120 across five windows, and with the per-card review summary beside it,
 * 1,099 requests from a single tab in 16 seconds against a 600/minute per-IP
 * ceiling.
 *
 * Events queue and go out together on `POST /analytics/events/batch` — a route
 * `analytics.controller.ts` has always had and nothing was using. NOTHING
 * about what is reported changes: same events, same order, same fields.
 *
 * WHY A SHORT TIMER AND NOT A SIZE. The events of one window arrive inside one
 * commit; a size trigger would either split a window or hold the last few
 * until the next scroll, and a shopper who leaves in between would take them
 * with them. 200 ms is longer than a commit and shorter than a scroll — and
 * `pagehide` flushes whatever is still queued, so leaving loses nothing.
 */
const STORE_EVENT_FLUSH_MS = 200;
let storeEventQueue = [];
let storeEventTimer = null;
let storeEventFlight = Promise.resolve();

function flushStoreEvents() {
  if (storeEventTimer) { clearTimeout(storeEventTimer); storeEventTimer = null; }
  const events = storeEventQueue;
  storeEventQueue = [];
  if (!events.length) return storeEventFlight;
  storeEventFlight = Promise.resolve()
    .then(() => API.post('/analytics/events/batch', { events }))
    .then(() => undefined)
    .catch(() => undefined);
  return storeEventFlight;
}

/**
 * Fire a funnel/analytics event. Used by the product browse flow to
 * track the funnel — view (in the grid), open (PDP), variant-modal-open,
 * cart-add — that feeds the owner's product insights page. Persistence is
 * background-only on the server (202 ACCEPTED), and we never await this
 * call from a user-blocking path. Failures are silent on purpose.
 *
 * Event types used by the product funnel (`product_view`,
 * `product_open`, `product_variant_modal`, `product_cart_add`) match the
 * keys ProductInsights' funnel block expects from the server.
 */
/**
 * THE VISITOR KEY, READ ONCE AND KEPT (2026-10-05). The key is read from storage for every event; the
 * event that matters most — the one sent as the tab closes — is the one a browser is likeliest to
 * refuse storage to. Read on the first event of the page session and kept in memory, so the closing
 * beacon carries the same key as the opening one and does no storage I/O at all.
 */
let knownVisitorKey = null;
export function visitorKeyForEvents() {
  if (!knownVisitorKey) {
    try { knownVisitorKey = getVisitorKey() || null; } catch (_) { knownVisitorKey = null; }
  }
  return knownVisitorKey || undefined;
}
/** Tests only. */
export function __forgetVisitorKey() { knownVisitorKey = null; }

export function trackStoreEvent({ salonId, eventType, payload }) {
  if (!salonId || !eventType) return;
  try {
    // THE SAME VISITOR KEY AS EVERY OTHER STEP. This read `localStorage.visitorKey`, a key nothing
    // ever wrote, so every product and checkout event of every shopper left without an id and
    // could not be joined to the journey that led to it — a week of DK checkouts (2026-09-23)
    // could say WHAT happened at the delivery step and not to WHOM. The shop's visitor key is
    // utils/visitorKey.js (`anonymousId`), the one the journey steps carry.
    const visitorKey = visitorKeyForEvents();
    queueStoreEvent({
      source: 'store',
      eventType,
      salonId,
      visitorKey,
      payload: payload || undefined,
    });
  } catch (_) {
    // never throw from instrumentation
  }
}

/**
 * One event, exactly as the caller built it, into the same window as every
 * other — for a caller that fills its own fields (utils/shopJourney.js sends
 * the visitor key the ad click was recorded under).
 */
export function queueStoreEvent(event) {
  if (!event || !event.salonId || !event.eventType) return;
  try {
    storeEventQueue.push(event);
    if (!storeEventTimer) {
      storeEventTimer = setTimeout(() => { storeEventTimer = null; flushStoreEvents(); }, STORE_EVENT_FLUSH_MS);
    }
  } catch (_) {
    // never throw from instrumentation
  }
}

/**
 * WHAT IS STILL QUEUED WHEN THE PAGE GOES AWAY LEAVES WITH THE PAGE'S LAST BREATH.
 *
 * A request started while a tab is closing is cancelled with it. A beacon would outlive the page,
 * but not with this body: `navigator.sendBeacon` cannot send `application/json` to another origin
 * without the browser refusing it or asking permission first, and the API parses nothing else
 * (`express.json` in stylon-server main.ts) — a `text/plain` beacon would arrive empty. So the
 * queue leaves by `fetch` with `keepalive`, which the browser also finishes after the page is gone
 * and which carries JSON across origins — the door utils/shopperSteps.js already sends a checkout's
 * step through as a tab closes. A browser without it, or a body over the keepalive limit, sends by
 * the ordinary request.
 */
export function flushStoreEventsOnExit() {
  if (storeEventTimer) { clearTimeout(storeEventTimer); storeEventTimer = null; }
  const events = storeEventQueue;
  if (!events.length) return;
  try {
    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
      const base = String((API && API.defaults && API.defaults.baseURL) || '/v1').replace(/\/+$/, '');
      const body = JSON.stringify({ events });
      // Browsers refuse a keepalive body over 64 KB (bytes, and a Hebrew letter is two or three of
      // them); the ordinary request below carries anything near that.
      if (body.length < 20000) {
        const sending = window.fetch(`${base}/analytics/events/batch`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body,
        });
        // Taken off the queue only once the browser has it: a refusal thrown here leaves it for the request below.
        storeEventQueue = [];
        Promise.resolve(sending).catch(() => {});
        return;
      }
    }
  } catch (_) {
    // the keepalive request was refused: the ordinary request below still tries
  }
  flushStoreEvents();
}

/** What has something to say as the page goes away, said before the queue leaves (utils/shopJourney.js's exit). */
const beforeLeaving = new Set();

/**
 * Be asked for a last event as the page is hidden or left — it goes out in the same beacon. The
 * listener is told which: `'hidden'` (the tab may come back) or `'pagehide'` (it is going for good),
 * so a page summary can be sent on the first and a "never continued" only on the second
 * (utils/shopMovement.js).
 */
export function beforeStoreEventsLeave(listener) {
  beforeLeaving.add(listener);
  return () => beforeLeaving.delete(listener);
}

function leaving(reason) {
  beforeLeaving.forEach((listener) => {
    try { listener(reason); } catch (_) { /* a listener's failure is its own */ }
  });
  flushStoreEventsOnExit();
}

/* Leaving the page must not take the queue with it. `pagehide` is the event
 * that actually fires on iOS Safari, where `unload` does not. */
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => leaving('pagehide'));
  document?.addEventListener?.('visibilitychange', () => {
    if (document.visibilityState === 'hidden') leaving('hidden');
  });
}

/** Test seam — send whatever is queued NOW and settle. Nothing in the app
 *  calls this; a suite that asserted on a timer would be a flaky suite. */
export function __flushStoreEvents() {
  return flushStoreEvents();
}

// —— Config (public) ——
export async function getSupportedCountries() {
  const { data } = await API.get('/config/supported-countries');
  return data;
}

/**
 * WHAT IS BEING CHANGED, ON THE WIRE — the one spelling, shared with the portal
 * and with the server (`editing-scope.ts`). A GET carries the two facts as
 * `editing=<appointmentId>` + `editingParts=<id,id>`; a POST carries the same
 * two facts as an object. Absent when this is a fresh booking, so a booking
 * request stays byte-identical to what it has always been.
 *
 * The wire NEVER names slot ids: it names the booking, and the server resolves
 * the rows itself, scoped to the salon in the URL. That is what makes it safe
 * to send from a customer's browser.
 */
function editingQueryParams(editing) {
  if (!editing || !editing.appointmentId) return {};
  const out = { editing: editing.appointmentId };
  if (Array.isArray(editing.partIds) && editing.partIds.length > 0) {
    out.editingParts = editing.partIds.join(',');
  }
  return out;
}

/** The same two facts, as a POST body field. */
export function editingBodyField(editing) {
  if (!editing || !editing.appointmentId) return {};
  return {
    editing: {
      appointmentId: editing.appointmentId,
      ...(Array.isArray(editing.partIds) && editing.partIds.length > 0
        ? { partIds: editing.partIds }
        : {}),
    },
  };
}

// —— Availability (public) – same contract as salon portal ——
export async function getAvailableSlots(salonId, specialistId, params) {
  const searchParams = new URLSearchParams();
  if (params.date) searchParams.set('date', params.date);
  if (params.duration != null) searchParams.set('duration', String(params.duration));
  if (params.granularity != null) searchParams.set('granularity', String(params.granularity));
  if (params.serviceIds != null && params.serviceIds.length > 0) {
    searchParams.set('serviceIds', Array.isArray(params.serviceIds) ? params.serviceIds.join(',') : params.serviceIds);
  }
  if (params.packageId) searchParams.set('packageId', params.packageId);
  if (params.includeOutOfWorkingHours === true) searchParams.set('includeOutOfWorkingHours', 'true');
  // A RESCHEDULE asks the same question a fresh booking asks, plus one fact:
  // which booking is moving. Without it the customer's own booking sits in the
  // diary the scan reads and counts as the reason it cannot be moved — the
  // 14:00 booking is never offered 14:30, and the sheet comes back short of an
  // hour with nothing on the screen to explain it.
  Object.entries(editingQueryParams(params.editing)).forEach(([k, v]) => searchParams.set(k, v));
  const query = searchParams.toString();
  const url = `/salons/${salonId}/specialists/${specialistId}/available-slots${query ? `?${query}` : ''}`;
  console.log('[SLOTS] store getAvailableSlots request', { salonId, specialistId, date: params.date, duration: params.duration, packageId: params.packageId, includeOutOfWorkingHours: params.includeOutOfWorkingHours });
  const { data } = await API.get(url);
  const list = Array.isArray(data) ? data : (data?.slots ?? data?.data ?? []);
  console.log('[SLOTS] store getAvailableSlots response', { salonId, specialistId, date: params.date, slotsCount: list.length });
  return data;
}

/**
 * SP5 multi-service availability: fetch appointment start times for a >1-service
 * cart where the customer may have picked a different specialist per service.
 *
 * Routes through the SAME public `available-slots` endpoint as the single path,
 * passing the per-service picks via the `servicePicks` query param (JSON-encoded
 * [{serviceId, specialistId}]) that the SP4 multi-service engine consumes. The
 * server returns slots whose start/end are the APPOINTMENT bounds and which
 * carry a `serviceAssignment` breakdown — we render them verbatim (no recompute).
 *
 * @param {string} salonId
 * @param {{ date: string, services: Array<{ serviceId: string, specialistId?: string }> }} body
 */
export async function getAvailableMultiServiceSlots(salonId, { date, services }) {
  const serviceIds = (services ?? []).map((s) => s.serviceId);
  // Only services with an explicit pick go into servicePicks; 'any'/omitted let
  // the engine choose.
  const picks = (services ?? [])
    .filter((s) => s.specialistId)
    .map((s) => ({ serviceId: s.serviceId, specialistId: s.specialistId }));
  const searchParams = new URLSearchParams();
  if (date) searchParams.set('date', date);
  if (serviceIds.length > 0) searchParams.set('serviceIds', serviceIds.join(','));
  if (picks.length > 0) searchParams.set('servicePicks', JSON.stringify(picks));
  const query = searchParams.toString();
  // specialistId path segment is 'any' — per-service narrowing is fully driven
  // by servicePicks; 'any' just means "no one-for-all pin".
  const url = `/salons/${salonId}/specialists/any/available-slots${query ? `?${query}` : ''}`;
  const { data } = await API.get(url);
  return data;
}

/**
 * Customer-facing GROUP availability (SP3). Solves start times for a group —
 * the booker + named guests, each with their own services — via the public
 * multi-customer slots endpoint (makespan solver; parallel where staff allow).
 * `participants` is slimmed to `[{ services: [{ serviceId, specialistId? }] }]`.
 *
 * `roomPreference` (Rooms feature P2, optional) — `'singles'` (default) seats
 * each participant in their own room; `'double'` (exactly 2 participants) asks
 * the solver to seat both together in one shared room with synchronized
 * specialists. Omitted entirely when not set so salons without rooms (or non-
 * room-attached groups) see byte-identical request bodies to before. The
 * server ignores it when rooms are off or no requested service is room-
 * attached, and simply returns no slots for an infeasible 'double' — callers
 * should treat an empty/'singles'-only response as "double not available".
 *
 * `coupled` (Couples feature, optional) — when true, requests a couples booking
 * (tightens availability constraints to co-available slots). Omitted entirely
 * when not set so salons without couples enabled see byte-identical request
 * bodies.
 *
 * `pairings` (Multi-person redesign P4, optional) — an INDEX-shaped list
 * `[{ participantIndices: [i, j] }]` (already resolved from client keys by the
 * caller via `pairingsToIndices`) marking which participants must share a room
 * + synchronized start on distinct specialists. Forwarded verbatim ONLY when
 * it's a non-empty array; omitted entirely otherwise so legacy (couple /
 * generic-group) request bodies stay byte-identical. Legacy `coupled`/
 * `roomPreference` are still sent alongside during the transition — the server
 * normalizes them into the same internal pairings model.
 *
 * Returns Array<{ startsAt, endsAt, makespanMin, perParticipant, isFastest, pairingResults? }>
 * where each leg may carry `assignedUnit: { resourceId, resourceSplitId?, name? }`
 * (name only present when the salon shows resource details to customers) and
 * `pairingResults?: [{ indices:[number,number], satisfied:boolean }]` reports
 * whether each requested pairing could actually be co-seated on that slot.
 */
export async function getMultiCustomerSlots(salonId, { date, participants, roomPreference, coupled, pairings } = {}) {
  const body = {
    date,
    participants: Array.isArray(participants) ? participants : [],
  };
  if (roomPreference === 'singles' || roomPreference === 'double') {
    body.roomPreference = roomPreference;
  }
  if (coupled) {
    body.coupled = true;
  }
  if (Array.isArray(pairings) && pairings.length > 0) {
    body.pairings = pairings;
  }
  const { data } = await API.post(`/salons/${salonId}/specialists/multi-customer-slots`, body);
  return data;
}

/**
 * WHY a day came back with no times.
 *
 * Called only after the slot list has already returned empty, so the path that
 * works pays nothing. Never throws — an explanation that fails must not break
 * the booking screen; the caller simply has nothing to add to its empty state.
 *
 * A customer never sees another customer's name here: the server reduces every
 * other booking to "Booked" for anyone who is not staff of the business.
 *
 * ONE DOOR: `legs` is a list of parts. One person is a list of one.
 */
export async function getWhyNoSlots(salonId, { date, legs } = {}) {
  if (!salonId || !date || !Array.isArray(legs) || legs.length === 0) return null;
  try {
    const { data } = await API.post(`/salons/${salonId}/specialists/why-no-slots`, { date, legs });
    return data || null;
  } catch (error) {
    console.log('[SLOTS] getWhyNoSlots failed', { error: error?.message ?? error });
    return null;
  }
}

/**
 * WHY IS THE SHEET EMPTY?
 *
 * "No times available" and nothing else is what makes a customer close the tab
 * and not rebook. A business can pick up the phone and ask; a customer just
 * gives up. So when no times come back, ask what is in the way and say it.
 *
 * Returns the reasons already named — `[{ kind, specialistName, resourceName,
 * fromLabel, toLabel, message }]` — where the clock times are in the BUSINESS's
 * timezone, decided by the side that knows which one that is. `kind` is what
 * each language renders; `message` is the server's English fallback.
 *
 * Same body shape as the slot request: one person is a party of one, never a
 * different question. Carries the editing scope too, so a customer moving their
 * own booking is never told that their own booking is the obstacle.
 *
 * NEVER THROWS INTO THE UI: a failure to explain an empty sheet must not turn
 * the empty sheet into an error. Callers get `[]`.
 */
export async function getAvailabilityBlockers(
  salonId,
  { date, participants, editing } = {},
) {
  if (!salonId) return [];
  try {
    const { data } = await API.post(`/salons/${salonId}/specialists/availability-blockers`, {
      date,
      participants: Array.isArray(participants) ? participants : [],
      ...editingBodyField(editing),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    return [];
  }
}

/** In-memory cache for available-days: 5 min TTL. Key = salonId|specialistId|from|to|duration|serviceIds|packageId */
const AVAILABLE_DAYS_CACHE_TTL_MS = 5 * 60 * 1000;
const availableDaysCache = new Map();

function availableDaysCacheKey(salonId, specialistId, params) {
  const serviceIds = params.serviceIds != null && params.serviceIds.length > 0
    ? (Array.isArray(params.serviceIds) ? params.serviceIds.join(',') : params.serviceIds)
    : '';
  return [String(salonId ?? ''), String(specialistId ?? ''), params.from, params.to, String(params.duration ?? ''), serviceIds, String(params.packageId ?? '')].join('|');
}

function availableDaysBulkCacheKey(salonId, specialistIds, params) {
  const ids = Array.isArray(specialistIds) ? [...specialistIds].sort().join(',') : '';
  const serviceIds = params.serviceIds != null && params.serviceIds.length > 0
    ? (Array.isArray(params.serviceIds) ? params.serviceIds.join(',') : params.serviceIds)
    : '';
  return ['bulk', String(salonId ?? ''), ids, params.from, params.to, String(params.duration ?? ''), serviceIds, String(params.packageId ?? '')].join('|');
}

/**
 * Returns an array of YYYY-MM-DD strings that have at least one available slot
 * for multiple specialists (union of all). Single HTTP call. Cached 5 min.
 * Use when "any" specialist is selected.
 */
export async function getAvailableDaysBulk(salonId, specialistIds, params) {
  if (!Array.isArray(specialistIds) || specialistIds.length === 0) {
    return [];
  }
  const key = availableDaysBulkCacheKey(salonId, specialistIds, params);
  const entry = availableDaysCache.get(key);
  if (entry && Date.now() <= entry.expiresAt) {
    if (typeof window !== "undefined") {
      console.log("[getAvailableDaysBulk] cache hit", { specialistCount: specialistIds.length, datesCount: entry.payload.length });
    }
    return entry.payload;
  }

  const t0 = typeof performance !== "undefined" ? performance.now() : 0;
  if (typeof window !== "undefined") {
    console.log("[getAvailableDaysBulk] request start", { salonId, specialistCount: specialistIds.length, from: params.from, to: params.to });
  }
  const searchParams = new URLSearchParams();
  searchParams.set('specialistIds', specialistIds.join(','));
  searchParams.set('from', params.from);
  searchParams.set('to', params.to);
  if (params.duration != null) searchParams.set('duration', String(params.duration));
  if (params.serviceIds != null && params.serviceIds.length > 0) {
    searchParams.set('serviceIds', Array.isArray(params.serviceIds) ? params.serviceIds.join(',') : params.serviceIds);
  }
  if (params.packageId) searchParams.set('packageId', params.packageId);
  const query = searchParams.toString();
  const url = `/salons/${salonId}/specialists/available-days-bulk${query ? `?${query}` : ''}`;
  const { data } = await API.get(url);
  const payload = Array.isArray(data) ? data : [];
  availableDaysCache.set(key, { payload, expiresAt: Date.now() + AVAILABLE_DAYS_CACHE_TTL_MS });
  if (typeof window !== "undefined") {
    const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
    console.log("[getAvailableDaysBulk] request done", { specialistCount: specialistIds.length, durationMs, datesCount: payload.length });
  }
  return payload;
}

/**
 * Returns an array of YYYY-MM-DD strings that have at least one available slot
 * for the given specialist, duration, and service in the date range.
 * For "any" specialist use getAvailableDaysBulk(salonId, specialistIds, params) instead (single request).
 * Results are cached for 5 minutes.
 */
export async function getAvailableDays(salonId, specialistId, params) {
  const key = availableDaysCacheKey(salonId, specialistId, params);
  const entry = availableDaysCache.get(key);
  if (entry && Date.now() <= entry.expiresAt) {
    if (typeof window !== "undefined") {
      console.log("[getAvailableDays] cache hit", { specialistId, datesCount: entry.payload.length });
    }
    return entry.payload;
  }

  const t0 = typeof performance !== "undefined" ? performance.now() : 0;
  if (typeof window !== "undefined") {
    console.log("[getAvailableDays] request start", { salonId, specialistId, from: params.from, to: params.to });
  }
  const searchParams = new URLSearchParams();
  searchParams.set('from', params.from);
  searchParams.set('to', params.to);
  if (params.duration != null) searchParams.set('duration', String(params.duration));
  if (params.serviceIds != null && params.serviceIds.length > 0) {
    searchParams.set('serviceIds', Array.isArray(params.serviceIds) ? params.serviceIds.join(',') : params.serviceIds);
  }
  if (params.packageId) searchParams.set('packageId', params.packageId);
  const query = searchParams.toString();
  const url = `/salons/${salonId}/specialists/${specialistId}/available-days${query ? `?${query}` : ''}`;
  const { data } = await API.get(url);
  const payload = Array.isArray(data) ? data : [];
  availableDaysCache.set(key, { payload, expiresAt: Date.now() + AVAILABLE_DAYS_CACHE_TTL_MS });
  if (typeof window !== "undefined") {
    const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
    console.log("[getAvailableDays] request done", { specialistId, durationMs, datesCount: payload.length });
  }
  return payload;
}

// —— Appointments (authenticated) ——
// Customer's appointments: use placeholder salonId in path; backend returns all customer appointments.
const CUSTOMER_APPOINTMENTS_PLACEHOLDER_SALON_ID = '00000000-0000-0000-0000-000000000000';

export async function getMyAppointments(params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.date) searchParams.set('date', params.date);
  if (params.specialistId) searchParams.set('specialistId', params.specialistId);
  const query = searchParams.toString();
  const url = `/salons/${CUSTOMER_APPOINTMENTS_PLACEHOLDER_SALON_ID}/appointments${query ? `?${query}` : ''}`;
  const { data } = await API.get(url);
  return data;
}

export async function getAppointmentById(appointmentId) {
  const { data } = await API.get(`/salons/${CUSTOMER_APPOINTMENTS_PLACEHOLDER_SALON_ID}/appointments/${appointmentId}`);
  return data;
}

/**
 * Confirm attendance for an approved appointment.
 * - With a one-tap `token` (from the WhatsApp Confirm deep link) → hits the
 *   public link endpoint, so it works even when the customer isn't logged in.
 * - Without a token (in-app "Confirm coming") → the authenticated endpoint.
 * Idempotent server-side.
 */
export async function confirmAttendance(appointmentId, token) {
  const base = `/salons/${CUSTOMER_APPOINTMENTS_PLACEHOLDER_SALON_ID}/appointments/${appointmentId}/confirm-attendance`;
  if (token) {
    const { data } = await API.post(`${base}/link`, { token });
    return data;
  }
  const { data } = await API.patch(base);
  return data;
}

export async function createAppointment(salonId, body) {
  const { data } = await API.post(`/salons/${salonId}/appointments`, body);
  return data;
}

// ── Customer-side card-payment booking flow ───────────────────────────────
// Flow:
//   1. createBookingDraft(...) on the customer side → gets draftId + amount.
//   2. Open TranzilaIframe with `pdesc=...d:<draftId>|s:<salonId>`.
//   3. Tranzila notify webhook (server-side) resolves the draft into a real
//      Appointment + Payment + SavedPaymentMethod.
//   4. FE polls getBookingDraft(draftId) until status flips to CONSUMED,
//      then navigates to the success screen with the linked appointmentId.
// See [stylon-server/src/modules/customer-payments/customer-bookings.controller.ts]
// for the full spec.

/**
 * The caller's own treatment plans.
 *
 * Live plans only, and a narrower shape than the clinic sees — what is wrong,
 * what is proposed, when it is happening and what it costs.
 */
export async function fetchMyPlans() {
  const { data } = await API.get('/me/plans');
  return Array.isArray(data?.plans) ? data.plans : [];
}

export async function fetchMyPlan(planId) {
  const { data } = await API.get(`/me/plans/${planId}`);
  return data;
}

export async function createBookingDraft(payload) {
  const { data } = await API.post(`/me/booking-drafts`, payload);
  return data;
}

export async function getBookingDraft(draftId) {
  const { data } = await API.get(`/me/booking-drafts/${draftId}`);
  return data;
}

/**
 * Browser-side iframe completion. Tranzila's notify webhook payload doesn't
 * include `tranzilatk` for our merchant, but the iframe's response_uri
 * redirect (which lands on /payments/iframe-callback and postMessages
 * the parent) DOES include it in the query string. We capture it on the
 * BE so the orchestrator can charge the real token instead of the
 * useless `gw_ref_<index>` fallback.
 */
export async function completeBookingDraftWithToken(draftId, body) {
  const { data } = await API.post(`/me/booking-drafts/${draftId}/complete-with-token`, body);
  return data;
}

/**
 * Mint a hosted-fields handshake token for the draft. Server returns
 * { thtk, terminal, amount, currency, tranMode } that the FE forwards to
 * Tranzila's `hfChargeApple` / `chargeGpay`. The handshake binds amount +
 * currency, so any later attempt to charge a different sum fails.
 */
export async function getHostedFieldsHandshake(draftId) {
  const { data } = await API.post(`/me/booking-drafts/${draftId}/hosted-fields-handshake`);
  return data;
}

/**
 * Persist the booking after a successful Tranzila hosted-fields charge.
 * The FE just got `transaction_response` from Tranzila's callback; we send
 * it verbatim and the server creates Appointment + Payment + SavedCard.
 */
export async function completeHostedFieldsCharge(draftId, transactionResponse, paymentMethod) {
  const { data } = await API.post(`/me/booking-drafts/${draftId}/complete-hosted-fields-charge`, {
    transactionResponse,
    paymentMethod,
  });
  return data;
}

/**
 * Read pending-extra-payment state for an appointment. Returns
 * { appointmentId, salonId, pendingExtraAmount, pendingExtraReason }.
 * pendingExtraAmount is null when nothing is owed.
 */
export async function getAppointmentPendingExtra(appointmentId) {
  const { data } = await API.get(`/me/appointments/${appointmentId}/pending-extra`);
  return data;
}

/**
 * Refund preview for the cancellation modal. Returns
 * `{ cashBooking, hasPolicy, notApprovedYet, totalPaid, willKeep, willRefund }`
 * so the modal can show "you'll be refunded ₪X / ₪Y will be kept per the
 * cancellation policy" — or a "full refund" line when the booking is still
 * pending biz approval. Routes through the same fee resolver the actual
 * cancel flow uses, so the preview matches what the customer actually
 * receives.
 */
export async function getCancellationRefundPreview(appointmentId) {
  const { data } = await API.get(`/me/appointments/${appointmentId}/cancellation-refund-preview`);
  return data;
}

/**
 * Mint Tranzila iframe params for paying down a pendingExtraAmount.
 * Returns { terminal, amount, currency, routingTag, salonId, appointmentId }.
 * The FE constructs the iframe URL from these and shows it in a modal.
 */
export async function createAppointmentPendingExtraPayIntent(appointmentId) {
  const { data } = await API.post(`/me/appointments/${appointmentId}/pending-extra/pay-intent`);
  return data;
}

/**
 * Mint a Tranzila hosted-fields handshake for the appointment's
 * pending-extra amount. Used by the customer pending-extra modal's
 * Apple Pay / Google Pay rows — same shape as the booking-flow
 * handshake, different source (Appointment.pendingExtraAmount, not a
 * draft). Returns `{ id, thtk, terminal, amount, currency, tranMode }`.
 */
// ─── A payment link (a POS LINK-mode intent) ────────────────────────────────
//
// Two doors, one link: the public door for whoever holds the link, the own
// door (`/me/pay/pos`) for a signed-in customer — their saved cards, and for a
// secured link the card form once their verified phone matched the link's.

/** The link as its payer sees it: the business, the figure, what it is for, the lines, and — when it may be paid — the card parameters. */
export async function getPosLink(intentId, { own = false } = {}) {
  const { data } = await API.get(own ? `/me/pay/pos/${intentId}` : `/pay/pos/${intentId}`);
  return data;
}

/** A receipt by its retrieval key: the business, the document, the lines, the payer, and the PDF's address. 404 for a key that opens nothing. */
export async function getReceipt(key) {
  const { data } = await API.get(`/public/receipts/${encodeURIComponent(key)}`);
  return data;
}

/** Apple Pay / Google Pay handshake for the link's own amount. */
export async function getPosLinkWalletHandshake(intentId, { own = false } = {}) {
  const { data } = await API.post(own ? `/me/pay/pos/${intentId}/wallet-handshake` : `/pay/pos/${intentId}/wallet-handshake`);
  return data;
}

/** Record a wallet charge against the link — the server checks it with the gateway first. */
export async function completePosLinkWalletCharge(intentId, transactionResponse, paymentMethod, { own = false } = {}) {
  const { data } = await API.post(
    own ? `/me/pay/pos/${intentId}/complete-wallet-charge` : `/pay/pos/${intentId}/complete-wallet-charge`,
    { transactionResponse, paymentMethod },
  );
  return data;
}

/** The details the receipt is made out to (a person's name/ID or a company and its number); the own door when signed in. */
export async function savePosLinkPayer(intentId, payer, { own = false } = {}) {
  const { data } = await API.put(own ? `/me/pay/pos/${intentId}/payer` : `/pay/pos/${intentId}/payer`, payer);
  return data;
}

/** Pay the link with one of the caller's own saved cards (the CVV only for a card saved at another business). */
export async function chargePosLinkWithSavedCard(intentId, savedPaymentMethodId, cvv) {
  const { data } = await API.post(`/me/pay/pos/${intentId}/charge-saved-card`, {
    savedPaymentMethodId,
    ...(cvv ? { cvv } : {}),
  });
  return data;
}

export async function getPendingExtraWalletHandshake(appointmentId) {
  const { data } = await API.post(
    `/me/appointments/${appointmentId}/pending-extra/wallet-handshake`,
  );
  return data;
}

/**
 * Persist a successful Tranzila hosted-fields AP/GP charge against the
 * appointment's pending-extra amount. Server validates ownership +
 * amount-matches-server, then creates the Payment + clears
 * `pendingExtraAmount` + credits the wallet atomically.
 */
export async function completePendingExtraWalletCharge(appointmentId, transactionResponse, paymentMethod) {
  const { data } = await API.post(
    `/me/appointments/${appointmentId}/pending-extra/complete-wallet-charge`,
    { transactionResponse, paymentMethod },
  );
  return data;
}

/**
 * Charge the appointment's pending-extra amount against one of the
 * customer's saved cards. Server resolves the amount itself from the
 * appointment (FE doesn't pass it) so the customer can't undercharge.
 *
 * On Cal-style decline the server throws `errorCode=CVV_REQUIRED` (400);
 * the caller catches, prompts for CVV, and replays this call with `cvv`
 * set. Any other failure surfaces as a normal axios error.
 *
 * Returns { alreadyResolved, captured, paymentId? }.
 */
export async function chargePendingExtraWithSavedCard(appointmentId, savedPaymentMethodId, cvv) {
  const body = { savedPaymentMethodId };
  if (cvv) body.cvv = cvv;
  const { data } = await API.post(
    `/me/appointments/${appointmentId}/pending-extra/charge-saved-card`,
    body,
  );
  return data;
}

/**
 * THE CERTIFICATES THIS STUDENT HOLDS — design §14, F-126.
 *
 * `MeCertificatesController` has served these since the PDF renderer landed and
 * no screen has ever called them: a student finished a course, was told a serial
 * number in a notification, and had nowhere to go with it. Revoked certificates
 * are excluded by the server, so this list is what they actually hold.
 */
export async function listMyCertificates() {
  const { data } = await API.get('/me/certificates');
  return Array.isArray(data) ? data : [];
}

/**
 * The document, as bytes.
 *
 * Not a URL: the server renders on demand precisely so a WITHDRAWN certificate
 * stops serving a document — a file on a CDN keeps working for ever for anyone
 * who ever had the link. `lang` sets the language of the template's own words
 * and is omitted by default, in which case the server matches the document's own
 * script; `forbidNonWhitelisted` covers the query string, so an empty string is
 * a 400 rather than a no-op.
 */
export async function downloadMyCertificate(certificateId, lang) {
  const { data } = await API.get(`/me/certificates/${certificateId}/pdf`, {
    responseType: 'blob',
    ...(lang ? { params: { lang } } : {}),
  });
  return data;
}

export async function listMyPayments(limit = 50) {
  const { data } = await API.get(`/me/payments?limit=${limit}`);
  return data;
}

/** Salon's cancellation + deposit policy (public). Returns
 *  { salonTiers: [...], serviceTiers: [...], effective, defaults: { ... } }.
 *  Null when the salon has no row — caller should treat as "no policy".
 *
 *  `serviceId` names the treatment being disclosed. Pass it whenever there IS a
 *  booked service: the server then answers with `effective` — the ladder/fee
 *  that will actually be charged for a leg of that service — resolved by the
 *  same helper the cancel charge uses. Without it the caller can only render
 *  the house ladder, which is what made the sheet promise the salon's numbers
 *  over a service that charges its own. */
export async function getCancellationPolicy(salonId, serviceId) {
  if (!salonId) return null;
  const { data } = await API.get(`/salons/${salonId}/cancellation-policy`, {
    params: serviceId ? { serviceId } : undefined,
  });
  return data;
}

export async function cancelAppointment(appointmentId, body = {}) {
  const { data } = await API.patch(
    `/salons/${CUSTOMER_APPOINTMENTS_PLACEHOLDER_SALON_ID}/appointments/${appointmentId}/cancel`,
    body
  );
  return data;
}

/** Add or edit customer note (pending/approved appointments only). Returns updated appointment. */
export async function updateAppointmentCustomerNote(appointmentId, customerNote) {
  const { data } = await API.patch(
    `/salons/${CUSTOMER_APPOINTMENTS_PLACEHOLDER_SALON_ID}/appointments/${appointmentId}/customer-note`,
    { customerNote: customerNote == null ? null : String(customerNote).trim() || null }
  );
  return data;
}

/** Reschedule appointment (customer or salon). Use real salonId from the appointment. */
export async function rescheduleAppointment(salonId, appointmentId, body) {
  const { data } = await API.patch(
    `/salons/${salonId}/appointments/${appointmentId}/reschedule`,
    body
  );
  return data;
}

/** Get booking recommendations for a customer + service (recommended date, favorite slots). Requires auth. */
export async function getBookingRecommendations(salonId, customerId, serviceId) {
  if (!salonId || !customerId || !serviceId) return null;
  const params = new URLSearchParams({ customerId, serviceId });
  const { data } = await API.get(
    `/salons/${salonId}/recommendations/booking-recommendations?${params.toString()}`
  );
  return data;
}

/** Get customer requirement records for a salon (for first_time_only "only once" logic). Requires auth. */
export async function getCustomerRequirementRecords(salonId, customerId, serviceIds = [], useGlobalCustomerId = true) {
  const params = new URLSearchParams();
  if (serviceIds.length) params.set('serviceIds', serviceIds.join(','));
  if (useGlobalCustomerId) params.set('useGlobalCustomerId', 'true');
  const query = params.toString();
  const { data } = await API.get(
    `/salons/${salonId}/appointments/requirement-records/${encodeURIComponent(customerId)}${query ? `?${query}` : ''}`
  );
  return data;
}

/** Save FCM token for web push (reminders, appointment updates). Call after getting token from Firebase. Pass null to clear. */
export async function saveFCMToken(token, options = {}) {
  const { data } = await API.patch('/notifications/fcm-token', {
    token: token ?? null,
    unregister: options.unregister ?? false,
  });
  return data;
}

// —— Luna Web Chat ——

/** Check if Luna web chat is available for this salon. Public — no auth required. */
export async function getLunaChatConfig(salonId) {
  const { data } = await API.get(`/salons/${salonId}/whatsapp-assistant/web-chat/config`);
  return data; // { available: boolean, assistantName?: string, greeting?: string }
}

/** Load the customer's prior web chat history from Redis. Requires customer JWT. */
export async function getLunaChatHistory(salonId) {
  const { data } = await API.get(`/salons/${salonId}/whatsapp-assistant/web-chat/history`);
  return data; // { turns: [{ role: 'user'|'assistant', content: string }] }
}

/** Send a message to Luna and receive her reply synchronously. Requires customer JWT. */
export async function sendLunaChatMessage(salonId, message) {
  const { data } = await API.post(`/salons/${salonId}/whatsapp-assistant/web-chat`, { message });
  return data; // { reply: string, conversationId: string, slots?: { date, slots: [{time, period}] } }
}

// ── Customer invoices & saved cards (subscription / one-time) ─────────────

/** List the logged-in customer's invoices and credit notes across every salon
 *  they've paid. The server joins by Customer.id → SalonCustomer rows →
 *  PosTransaction.customerId → Invoice (so a phone collision can't leak
 *  invoices between users on a shared device). */
export async function listMyInvoices(limit = 100) {
  const { data } = await API.get('/me/invoices', { params: { limit } });
  return data;
}

/**
 * List the customer's Stylon-issued tax documents (Tranzila-emitted).
 * Returns `{ items: [{ id, kind, reason, state, amount, currency, pdfUrl,
 * issuedAt, emailedAt, appointmentId, appointmentStartsAt, subscriptionId }] }`.
 * Use this for online-booking receipts; the POS-side `listMyInvoices` covers
 * salon-counter sales (rarely relevant for web-store customers).
 */
export async function listMyBillingDocuments(limit = 100) {
  const { data } = await API.get('/me/billing-documents', { params: { limit } });
  return data?.items || [];
}

/** Late email capture for a specific billing document (one-off use; PATCH /auth/me is the canonical path). */
export async function attachEmailToBillingDocument(id, email) {
  const { data } = await API.post(`/billing-documents/${id}/email`, { email });
  return data;
}

/** List the customer's saved cards (Tranzilla tokens, brand + last4 only). */
export async function listMySavedCards() {
  const { data } = await API.get('/me/payment-methods');
  return data;
}

/** Save a new card. The PAN/CVV go straight to the gateway for tokenization;
 *  this endpoint persists only { gatewayMethodRef, brand, last4, exp* }. */
export async function saveMyCard(payload) {
  const { data } = await API.post('/me/payment-methods', payload);
  return data;
}

export async function setMyCardDefault(id) {
  const { data } = await API.post(`/me/payment-methods/${id}/set-default`);
  return data;
}

export async function deleteMyCard(id) {
  const { data } = await API.delete(`/me/payment-methods/${id}`);
  return data;
}

// —— Waitlist ——

// Join the waitlist for the chosen specialist/service on a given date.
// body: { specialistId, serviceId?, packageId?, preferredDate: 'YYYY-MM-DD', windows: [{fromMin, toMin}, ...] }
export async function joinWaitlist(salonId, body) {
  const { data } = await API.post(`/salons/${salonId}/waitlist`, body);
  return data;
}

// Default time-window suggestion for the modal (biggest unavailable gap).
export async function getWaitlistLargestGap(salonId, { specialistId, date, durationMin }) {
  const { data } = await API.get(`/salons/${salonId}/waitlist/largest-gap`, {
    params: { specialistId, date, durationMin },
  });
  return data; // { fromMin, toMin } | null
}

// My own ACTIVE entries across all salons — used by Profile -> My Waitlist tab.
export async function getMyWaitlistEntries() {
  const { data } = await API.get('/me/waitlist');
  return data;
}

// Cancel one of my entries.
export async function cancelWaitlistEntry(salonId, entryId) {
  const { data } = await API.delete(`/salons/${salonId}/waitlist/${entryId}`);
  return data;
}

/**
 * Static compat check for service-picker disable UX.
 * Returns a map: { [candidateServiceId]: true|false }.
 * Anonymous endpoint — no auth required.
 */
export async function checkServiceCompat(salonId, pickedServiceIds, candidateServiceIds) {
  if (!salonId || !candidateServiceIds?.length) return {};
  const { data } = await API.post(`/salons/${salonId}/availability/compat`, {
    pickedServiceIds: pickedServiceIds || [],
    candidateServiceIds,
  });
  return data;
}

// ============================================================================
// PRODUCTS — public endpoints, no auth (Wave C)
// ============================================================================

/**
 * List active products for a salon (by slug).
 *
 * `brandIds` narrows AT SOURCE. The route takes `?brandId=a,b` — comma
 * separated, OR within the facet — and that is what makes the shop's brand
 * filter scale: it used to load every product and filter the array in the
 * browser, so it could only ever be as complete as one page of the catalogue.
 * Verified against the running API (`stylon_test`, `e2e-test-salon`): 7
 * products unfiltered, 3 for لوريال alone, 5 for both seeded brands.
 *
 * An empty or absent list sends no parameter at all, so the unfiltered call is
 * byte-for-byte the request it always was.
 */
export async function fetchPublicProducts(salonSlug, { brandIds } = {}) {
  if (!salonSlug) return [];
  const list = (Array.isArray(brandIds) ? brandIds : [brandIds])
    .filter((b) => typeof b === 'string' && b.trim());
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/products`, {
      params: list.length ? { brandId: list.join(',') } : undefined,
    });
    return Array.isArray(data) ? data.map(applyNameAsTypeAxis) : [];
  } catch (err) {
    if (err?.response?.status === 404) return [];
    throw err;
  }
}

/**
 * ── THE SHOP LOADS A LARGE CATALOGUE A WINDOW AT A TIME ──────────────────────
 *
 * `fetchPublicProducts` above is unchanged and stays unchanged: it is the
 * request the route has always answered, and other surfaces still make it.
 *
 * What it costs is the problem. `dk-store` is a real merchant with 529
 * products; that one request is 3.63 MB and the page paints 529 cards with
 * 2 116 images before the shopper has scrolled. So the shop now asks two
 * different questions instead of one:
 *
 *   THE SHAPE — `fetchPublicProductIndex`. Every product, but only the fields
 *   the shop NARROWS, SORTS, FACETS and COUNTS on: the ids, the names, the
 *   prices, the stock, the category, the brand, the variant axes. 384 KB for
 *   dk-store instead of 3.63 MB, because the descriptions, their translations
 *   and every image url are left behind. `utils/shopFilters` — the one
 *   description of "what is narrowed", shared with the collection page — runs
 *   over this UNCHANGED, so every filter, the sort and the shareable URL
 *   behave exactly as they did, and "N products" is a real count of the real
 *   answer rather than a count of what happens to be downloaded.
 *
 *   THE CONTENTS — `fetchPublicProductWindow`. Given the ordered, filtered id
 *   list the shop computed from the shape, fetch the CARDS for a window of it.
 *   Asking by id rather than by offset is what makes this safe while the
 *   merchant is editing: an offset window silently skips and repeats rows when
 *   a product is inserted or removed under it, and a named window cannot.
 *
 * `fetchPublicProductPage` is the third, and it is the fast path for first
 * paint: the top of an unfiltered shop is knowable without the shape, so the
 * grid can be on screen while the index is still arriving.
 */

/** One request may name this many ids — the server's own window cap. */
const PRODUCT_WINDOW_MAX = 60;
/** Index rows are ~24× smaller than cards, so a page of them is much larger. */
const PRODUCT_INDEX_PAGE = 1000;
/** A cursor loop needs a floor. 25 × 1000 is far past any real catalogue. */
const PRODUCT_INDEX_MAX_PAGES = 25;

/**
 * Read one page of the product list.
 *
 * Answers `{ items, nextCursor, total }` whatever the server sent: an older or
 * cached API answering the bare array is read as a single complete page rather
 * than thrown, because a shop that renders nothing is a worse failure than a
 * shop that stops paging.
 */
export async function fetchPublicProductPage(salonSlug, {
  limit, cursor, fields, brandIds, homepageStrip, ids,
} = {}) {
  const empty = { items: [], nextCursor: null, total: 0 };
  if (!salonSlug) return empty;
  const brands = (Array.isArray(brandIds) ? brandIds : [brandIds])
    .filter((b) => typeof b === 'string' && b.trim());
  const params = {};
  if (limit != null) params.limit = limit;
  if (cursor) params.cursor = cursor;
  if (fields) params.fields = fields;
  if (ids) params.ids = ids;
  if (brands.length) params.brandId = brands.join(',');
  if (homepageStrip === true) params.homepageStrip = true;
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/products`, { params });
    if (Array.isArray(data)) {
      const items = data.map(applyNameAsTypeAxis);
      return { items, nextCursor: null, total: items.length };
    }
    const items = Array.isArray(data?.items) ? data.items.map(applyNameAsTypeAxis) : [];
    return {
      items,
      nextCursor: data?.nextCursor ?? null,
      total: typeof data?.total === 'number' ? data.total : items.length,
    };
  } catch (err) {
    if (err?.response?.status === 404) return empty;
    throw err;
  }
}

/**
 * The whole catalogue's SHAPE, following the cursor until it runs out.
 *
 * Bounded on purpose: a server that always answers a cursor would otherwise
 * turn this into an endless client, and an endless client on a storefront is
 * a browser tab that never settles.
 */
export async function fetchPublicProductIndex(salonSlug, { brandIds } = {}) {
  if (!salonSlug) return [];
  const out = [];
  let cursor = null;
  for (let page = 0; page < PRODUCT_INDEX_MAX_PAGES; page++) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchPublicProductPage(salonSlug, {
      fields: 'index', limit: PRODUCT_INDEX_PAGE, cursor, brandIds,
    });
    out.push(...res.items);
    cursor = res.nextCursor;
    if (!cursor) break;
  }
  return out;
}

/**
 * The CARDS for exactly these ids, in exactly this order.
 *
 * The order is the shopper's — their sort, their filters — so it is asserted
 * here rather than trusted to the response: a batch that came back in a
 * different order would silently reshuffle the grid.
 */
export async function fetchPublicProductWindow(salonSlug, ids) {
  const wanted = (Array.isArray(ids) ? ids : []).filter(Boolean);
  if (!salonSlug || !wanted.length) return [];
  const batches = [];
  for (let i = 0; i < wanted.length; i += PRODUCT_WINDOW_MAX) {
    batches.push(wanted.slice(i, i + PRODUCT_WINDOW_MAX));
  }
  const pages = await Promise.all(
    batches.map((batch) => fetchPublicProductPage(salonSlug, { ids: batch.join(',') })),
  );
  const byId = new Map();
  for (const page of pages) for (const row of page.items) byId.set(row.id, row);
  return wanted.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * How many products this shop sells, without downloading them.
 *
 * The business home page used to answer this by fetching the entire catalogue
 * and reading `.length` — 3.63 MB to print one number on dk-store.
 */
export async function fetchPublicProductCount(salonSlug) {
  try {
    const { total } = await fetchPublicProductPage(salonSlug, { fields: 'index', limit: 1 });
    return typeof total === 'number' ? total : 0;
  } catch (_) {
    return 0;
  }
}

/** Get one product by id within a salon (by slug). */
export async function fetchPublicProduct(salonSlug, productId) {
  if (!salonSlug || !productId) return null;
  // The page's own HTML asked for this product before the app arrived (public/index.html): one answer, read once.
  const early = typeof window !== 'undefined' ? window.__stylonProductPreload : null;
  if (early && early.key === `${salonSlug}/${productId}` && early.promise) {
    window.__stylonProductPreload = null;
    const data = await early.promise.catch(() => null);
    if (data) return applyNameAsTypeAxis(data);
  }
  /* ESCAPED. A product id reaches this from the address bar, and this file
   * interpolates path segments raw; `productIdFromParam` now refuses anything
   * that is not id-shaped, and this is the second lock on the same door. */
  const { data } = await API.get(
    `/public/salons/${encodeURIComponent(salonSlug)}/products/${encodeURIComponent(productId)}`,
  );
  return applyNameAsTypeAxis(data);
}

/**
 * THE HAND-OFF TOKEN (2026-10-06, utils/openInBrowser.js). Inside an app's browser the wallet button sends the
 * shopper to Safari or Chrome; this keeps what they typed and chose on the server under a one-time token so the
 * real browser can open the checkout filled in. Answers `{ token, expiresAt }`. The caller bounds the wait and falls
 * back to the basket in the address when this fails — nothing here retries or swallows.
 */
/**
 * A shopper who could not open WhatsApp from where they are leaves a number; the shop is told and messages them
 * (Components/ContactOrderFallback). The number travels in this one request and never into an analytics event.
 */
export async function requestShopContact(salonSlug, payload) {
  const { data } = await API.post(`/public/salons/${salonSlug}/contact-request`, payload);
  return data;
}

export async function createCheckoutHandoff(salonSlug, payload) {
  const { data } = await API.post(`/public/salons/${salonSlug}/checkout-handoff`, payload);
  return data;
}

/**
 * The payload a hand-off token names — ONCE. The server answers 410 to a second read and to a token older than
 * fifteen minutes, 404 to another shop's or a never-minted one; every refusal throws, with `response.status` for
 * the caller to read.
 */
export async function fetchCheckoutHandoff(salonSlug, token) {
  const { data } = await API.get(`/public/salons/${salonSlug}/checkout-handoff/${encodeURIComponent(token)}`);
  return data;
}

/**
 * Place an order against a salon (public — no auth required).
 *
 * Returns the created Order (with items + delivery + first OrderStatusEvent).
 */
export async function createOrder(salonSlug, dto) {
  const body = { visitorKey: getVisitorKey(), ...dto };
  /* THE PIXEL'S COOKIES RIDE AS HEADERS, NEVER IN THE BODY — the body refuses fields it does not
   * know (see utils/metaPixelCookies). Only when the browser holds them.
   *
   * DEPLOY ORDER. A browser asks the API's preflight before it sends a header like these, and
   * the API must name them in its CORS `allowedHeaders` or EVERY order fails as a bare "Network
   * Error". The server allows them from 624b5445a; this storefront must not go live ahead of that
   * server — the reverse of the one-deploy-behind retry below. */
  const pixelHeaders = metaPixelCookieHeaders();
  const post = (payload) => (Object.keys(pixelHeaders).length
    ? API.post(`/public/salons/${salonSlug}/orders`, payload, { headers: pixelHeaders })
    : API.post(`/public/salons/${salonSlug}/orders`, payload));
  try {
    const { data } = await post(body);
    return data;
  } catch (err) {
    // ONE DEPLOY BEHIND. The storefront goes live minutes before the server; a
    // server that does not know `expectedTotal` yet refuses the whole body
    // ("property expectedTotal should not exist"). Place the order without the
    // price-change check rather than take no orders at all. Nothing else retries.
    const messages = [].concat(err?.response?.data?.message ?? []);
    const unknownExpectedTotal = err?.response?.status === 400
      && 'expectedTotal' in body
      && messages.some((m) => /property expectedTotal should not exist/.test(String(m)));
    if (!unknownExpectedTotal) throw err;
    const { expectedTotal, ...withoutIt } = body;
    const { data } = await post(withoutIt);
    return data;
  }
}

// ─── Likes / hearts ──────────────────────────────────────────────────────────

/** Public: get total like count + (when authed) whether the current user likes it. */
export async function getProductLikeState(productId) {
  if (!productId) return { liked: false, count: 0 };
  try {
    const { data } = await API.get(`/public/products/${productId}/likes`);
    return data;
  } catch (_) {
    return { liked: false, count: 0 };
  }
}

/** Authed: heart a product. Returns { liked, count }. Idempotent. */
export async function likeProduct(productId) {
  const { data } = await API.post(`/products/${productId}/like`);
  return data;
}

/** Authed: remove your heart from a product. Returns { liked, count }. */
export async function unlikeProduct(productId) {
  const { data } = await API.delete(`/products/${productId}/like`);
  return data;
}

export async function getSalonLikeState(salonId) {
  if (!salonId) return { liked: false, count: 0 };
  try {
    const { data } = await API.get(`/public/salons/${salonId}/likes`);
    return data;
  } catch (_) {
    return { liked: false, count: 0 };
  }
}

export async function likeSalon(salonId) {
  const { data } = await API.post(`/salons/${salonId}/like`);
  return data;
}

export async function unlikeSalon(salonId) {
  const { data } = await API.delete(`/salons/${salonId}/like`);
  return data;
}

export async function getMyLikedSalons({ limit = 20 } = {}) {
  try {
    const { data } = await API.get('/me/liked-salons', { params: { limit } });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

export async function getMyLikedProducts({ limit = 20 } = {}) {
  try {
    const { data } = await API.get('/me/liked-products', { params: { limit } });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

// ─── My orders (customer) ────────────────────────────────────────────────────

export async function getMyOrders() {
  try {
    const { data } = await API.get('/me/orders');
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/** Whether a customer token is held — a signed-out visitor's page asks nothing of /me or /my (10-21 known: 401s). */
function hasCustomerToken() {
  try {
    return Boolean(localStorage.getItem('authToken') || localStorage.getItem('accessToken'));
  } catch (_) {
    return false;
  }
}

/** Recent order line items for buy-again strips (optional salonSlug scope). */
export async function getMyRecentOrderItems({ salonSlug, limit = 10 } = {}) {
  if (!hasCustomerToken()) return [];
  try {
    const params = {};
    if (salonSlug) params.salonSlug = salonSlug;
    if (limit) params.limit = limit;
    const { data } = await API.get('/me/orders/recent-items', { params });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/**
 * TAKE A GUEST ORDER THIS ACCOUNT'S OWN PHONE PLACED.
 *
 * A guest checkout files the order with no owner, and signing up alone never
 * claims it — the number on it was typed by somebody who proved nothing. This
 * asks for ONE order by id, which only the browser that placed it holds; the
 * server hands it over only when the account's PROVEN phone is the one on it.
 *
 * Answers `false` rather than throwing: it is offered on a page that has
 * already told somebody their order went through, and must never break it.
 */
export async function claimMyOrder(orderId) {
  if (!orderId) return false;
  try {
    const { data } = await API.post(`/me/orders/${orderId}/claim`);
    return data?.claimed === true;
  } catch (_) {
    return false;
  }
}

export async function getMyOrder(orderId) {
  if (!orderId) return null;
  const { data } = await API.get(`/me/orders/${orderId}`);
  return data;
}

/**
 * WHERE THE MONEY STANDS ON AN ORDER THIS DEVICE PLACED (2026-09-29).
 *
 * A guest's silent wallet used to be checked through `getMyOrder`, a signed-in
 * route: six 401s, and a payment that may have gone through was called a
 * failure (DK, 27 Sept, Instagram's browser). This is the public read: the
 * visitor key the order was placed with is the proof (`createOrder` sends the
 * same key), and the answer is the money only —
 * `{ orderId, orderStatus, paymentStatus, paidNow, total, currency, paidFor: { full, deposit } }`.
 * A key that did not place the order gets a 404, like an unknown id.
 */
export async function getOrderPaymentState(salonSlug, orderId) {
  if (!salonSlug || !orderId) return null;
  const { data } = await API.get(`/public/salons/${salonSlug}/orders/${orderId}/payment-state`, {
    params: { visitorKey: getVisitorKey() },
  });
  return data;
}

/**
 * What the customer may still do to their own order — `{ mayEditItems,
 * mayEditDelivery, status }`, plus `mayCancel` / `refundAmount` when the
 * server states them. `null` when it cannot be read: a caller draws no control
 * it cannot vouch for, rather than a button that 400s.
 */
export async function getMyOrderCapabilities(orderId) {
  if (!orderId) return null;
  try {
    const { data } = await API.get(`/me/orders/${orderId}/can-edit`);
    return data && typeof data === 'object' ? data : null;
  } catch (_) {
    return null;
  }
}

/** Customer adds/updates a note on their order (editable until handoff). */
export async function setMyOrderNote(orderId, note) {
  const { data } = await API.patch(`/me/orders/${orderId}/note`, { note });
  return data;
}

// ─── Saved delivery addresses (cross-device sync) ─────────────────────────
// The web-store previously kept saved addresses ONLY in localStorage, so a
// customer switching phones/browsers silently lost every saved address.
// These endpoints back a real per-customer store; checkoutProfile.js keeps
// localStorage as a fast-loading cache and reconciles with these in the
// background. IDs are shared verbatim between the local cache and the
// server row (client generates them), so no id-remapping is needed.

/**
 * List the logged-in customer's saved delivery addresses (server copy).
 *
 * Deliberately does NOT swallow failures into an empty array: the reconcile
 * treats "the server doesn't have this address" as "it was deleted on another
 * device, drop it locally". A failed request that looked like an empty list
 * would therefore wipe every synced address on this device. Callers must
 * catch and leave the local cache alone.
 */
export async function listMySavedAddresses() {
  const { data } = await API.get('/me/addresses');
  if (!Array.isArray(data)) throw new Error('Unexpected /me/addresses response');
  return data;
}

/** Create (no id) or update (with id) a saved delivery address on the server. */
export async function syncSavedAddress(address) {
  const { data } = await API.post('/me/addresses', address);
  return data;
}

/** Bump an address's lastUsedAt on the server (mirrors the local touchSavedAddress). */
export async function touchMySavedAddress(id) {
  if (!id) return null;
  try {
    const { data } = await API.post(`/me/addresses/${id}/touch`);
    return data;
  } catch (_) {
    return null;
  }
}

export async function deleteMySavedAddress(id) {
  if (!id) return;
  try {
    await API.delete(`/me/addresses/${id}`);
  } catch (_) {
    // best-effort — local delete already happened
  }
}

// ─── Wishlist (customer wants a specific variant, often OOS) ─────────────────

export async function addToWishlist(productId, variantId) {
  const { data } = await API.post(`/products/${productId}/wishlist`, { variantId });
  return data;
}
export async function removeFromWishlist(productId, variantId) {
  const { data } = await API.delete(`/products/${productId}/wishlist`, { data: { variantId } });
  return data;
}
export async function getMyWishlist() {
  try {
    const { data } = await API.get('/me/wishlist');
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

// ─── Reviews ─────────────────────────────────────────────────────────────────

export async function getProductReviewSummary(productId) {
  try {
    const { data } = await API.get(`/public/products/${productId}/reviews/summary`);
    return data ?? { count: 0, average: 0, histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
  } catch (_) {
    return { count: 0, average: 0, histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
  }
}

export async function listProductReviews(productId, opts = {}) {
  try {
    const params = {};
    if (opts.sort) params.sort = opts.sort;
    if (opts.limit) params.limit = opts.limit;
    const { data } = await API.get(`/public/products/${productId}/reviews`, { params });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

export async function postReviewForOrderItem(orderItemId, { stars, body, imageUrls, imageUrl }) {
  const pics = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  const payload = { stars, body };
  if (pics.length) {
    payload.imageUrls = pics;
    payload.imageUrl = pics[0]; // back-compat: first photo also lands in the legacy column
  } else if (imageUrl) {
    payload.imageUrl = imageUrl;
  }
  const { data } = await API.post(`/order-items/${orderItemId}/review`, payload);
  return data;
}

/** Upload a single review photo (multipart). Returns { url }. Reuses the
 *  customer-photo upload route (any logged-in customer). */
export async function uploadReviewPhoto(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await API.post('/upload/customer-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function toggleReviewHelpful(reviewId) {
  const { data } = await API.post(`/reviews/${reviewId}/helpful`);
  return data;
}

// ─── Appointment reviews ─────────────────────────────────────────────────────
// Mirror of the product-review fns above, against the appointment-review
// endpoints. A salon's public ratings (on its biz page) are gated server-side
// on `appointmentReviewsPublic`; writing a review is gated on
// `appointmentReviewsEnabled` + the appointment being COMPLETED + owned.

const EMPTY_SUMMARY = { count: 0, average: 0, histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };

/** Build the `{ sort, limit, serviceId, specialistId }` query params, omitting
 *  empties so the server applies its defaults. Shared by the salon + specialist
 *  list fns. */
function buildAppointmentReviewParams(opts = {}) {
  const params = {};
  if (opts.sort) params.sort = opts.sort;
  if (opts.limit) params.limit = opts.limit;
  if (opts.serviceId) params.serviceId = opts.serviceId;
  if (opts.specialistId) params.specialistId = opts.specialistId;
  return params;
}

export async function getSalonReviewSummary(salonId, opts = {}) {
  if (!salonId) return { ...EMPTY_SUMMARY };
  try {
    const { data } = await API.get(`/public/salons/${salonId}/reviews/summary`, {
      params: buildAppointmentReviewParams(opts),
    });
    return data ?? { ...EMPTY_SUMMARY };
  } catch (_) {
    return { ...EMPTY_SUMMARY };
  }
}

export async function listSalonReviews(salonId, opts = {}) {
  if (!salonId) return [];
  try {
    const { data } = await API.get(`/public/salons/${salonId}/reviews`, {
      params: buildAppointmentReviewParams(opts),
    });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

export async function getSpecialistReviewSummary(specialistId, opts = {}) {
  if (!specialistId) return { ...EMPTY_SUMMARY };
  try {
    const { data } = await API.get(`/public/specialists/${specialistId}/reviews/summary`, {
      params: buildAppointmentReviewParams(opts),
    });
    return data ?? { ...EMPTY_SUMMARY };
  } catch (_) {
    return { ...EMPTY_SUMMARY };
  }
}

export async function listSpecialistReviews(specialistId, opts = {}) {
  if (!specialistId) return [];
  try {
    const { data } = await API.get(`/public/specialists/${specialistId}/reviews`, {
      params: buildAppointmentReviewParams(opts),
    });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

export async function postAppointmentReview(appointmentId, { stars, body, imageUrls, imageUrl }) {
  const pics = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  const payload = { stars, body };
  if (pics.length) {
    payload.imageUrls = pics;
    payload.imageUrl = pics[0]; // back-compat: first photo also lands in the legacy column
  } else if (imageUrl) {
    payload.imageUrl = imageUrl;
  }
  const { data } = await API.post(`/appointments/${appointmentId}/review`, payload);
  return data;
}

export async function toggleAppointmentReviewHelpful(reviewId) {
  const { data } = await API.post(`/appointment-reviews/${reviewId}/helpful`);
  return data;
}

// ─── The business's own rating (appointment + product reviews, pooled) ───────
// One door, two callers: the shop page holds the salon ID, the product page
// holds the slug, and `/public/salons/:salonIdOrSlug/rating` answers to both.
//
// It NEVER carries the Google rating. That one arrives through
// `api/googleReviews` and is drawn with Google's own attribution beside it —
// see `utils/businessRating.js` for why the two must not be added together.

const EMPTY_RATING = {
  count: 0,
  average: 0,
  histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  sources: { appointments: { count: 0, average: 0 }, products: { count: 0, average: 0 } },
};

export async function getBusinessRating(salonIdOrSlug) {
  if (!salonIdOrSlug) return { ...EMPTY_RATING };
  try {
    const { data } = await API.get(`/public/salons/${encodeURIComponent(salonIdOrSlug)}/rating`);
    return data ?? { ...EMPTY_RATING };
  } catch (_) {
    // A rating is the least important thing on a shop page: a shop whose
    // rating endpoint is unreachable still has to render its products.
    return { ...EMPTY_RATING };
  }
}

/**
 * THE SHOP'S OWN REVIEWS — the ones the business transcribed from its Google
 * listing — newest first, with the count and the mean. The product page does
 * NOT call this on first paint: the three it shows arrive inside the salon
 * payload (`salon.shopReviews.sample`). This is the "See all" request.
 */
export async function listShopReviews(salonSlug, opts = {}) {
  if (!salonSlug) return { count: 0, average: 0, reviews: [] };
  try {
    const params = {};
    if (opts.limit) params.limit = opts.limit;
    const { data } = await API.get(`/public/salons/${encodeURIComponent(salonSlug)}/shop-reviews`, { params });
    return data && Array.isArray(data.reviews) ? data : { count: 0, average: 0, reviews: [] };
  } catch (_) {
    return { count: 0, average: 0, reviews: [] };
  }
}

export async function listBusinessReviews(salonIdOrSlug, opts = {}) {
  if (!salonIdOrSlug) return [];
  try {
    const params = {};
    if (opts.limit) params.limit = opts.limit;
    if (opts.source && opts.source !== 'ALL') params.source = opts.source;
    const { data } = await API.get(
      `/public/salons/${encodeURIComponent(salonIdOrSlug)}/rating/reviews`, { params },
    );
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/**
 * The reviews the SHOP panel shows: product reviews first, topped up with
 * service ones.
 *
 * The owner's rule — "prioritize the ratings on the products rather than the
 * appointments.. because this biz have both". A shop panel that leads with
 * haircut reviews on a page selling serums is answering the wrong question,
 * but a shop with no product reviews yet must not show an empty section
 * either, so the remainder comes from appointments.
 *
 * No server change: `source` already narrows the same endpoint, and every row
 * carries `kind` and `subject`, which is what lets each card say what it is
 * about rather than leaving the reader to guess.
 */
export async function listShopPanelReviews(salonIdOrSlug, limit = 5) {
  if (!salonIdOrSlug) return [];
  const products = await listBusinessReviews(salonIdOrSlug, { limit, source: 'PRODUCTS' });
  if (products.length >= limit) return products.slice(0, limit);
  const appointments = await listBusinessReviews(salonIdOrSlug, {
    limit: limit - products.length,
    source: 'APPOINTMENTS',
  });
  return [...products, ...appointments].slice(0, limit);
}

// ─── Shipping ─────────────────────────────────────────────────────────────────

/**
 * Resolve pickup/delivery options + payment toggles for a salon + cart.
 *
 * `null` when the call fails. A basket holding an item the shop no longer sells
 * is not a failure: the server refuses to price it (400 ITEM_UNAVAILABLE, naming
 * the product; ORDER_BY_ASKING when every refused line is sold by asking the shop),
 * and that comes back as `{ refusal }` so the checkout can say which item —
 * instead of standing in a quote for a basket it cannot sell.
 */
export async function resolveShippingOptions(salonSlug, body) {
  if (!salonSlug) return null;
  try {
    const { data } = await API.post(`/public/salons/${salonSlug}/shipping-options`, body);
    return data;
  } catch (err) {
    const refused = err?.response?.data;
    const code = refused?.errorCode ?? refused?.message?.errorCode;
    return code === 'ITEM_UNAVAILABLE' || code === 'ORDER_BY_ASKING' ? { refusal: err } : null;
  }
}

/** Shop-page banners (free delivery promo, etc.). */
export async function fetchShopPromotions(salonSlug) {
  if (!salonSlug) return { freeDelivery: null };
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/shop-promotions`);
    return data ?? { freeDelivery: null };
  } catch (_) {
    return { freeDelivery: null };
  }
}

// ─── Product collections (shop.app-style row, collection page, filter) ──────

/** Visible collections for the shop row + all-collections page, ordered. */
export async function fetchCollections(salonSlug) {
  if (!salonSlug) return [];
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/collections`);
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/**
 * Record a product-collection analytics event
 * (IMPRESSION | OPEN | PRODUCT_CLICK | ADD_TO_CART) — "did this collection
 * work?" for the operator. Public, no auth, same posture as
 * `recordCampaignClick`: the server proves the collection id belongs to
 * `salonId` before writing anything.
 *
 * Fire-and-forget from a `setTimeout(0)`, like `trackStoreEvent` — analytics
 * must NEVER block a render or break a page, so a failed post is swallowed,
 * never surfaced to the shopper.
 */
/**
 * THE "HOT" LABEL'S TWO CALLS.
 *
 * `fetchHotProducts` is a public GET returning at most twenty-four ids — a few
 * hundred bytes, fetched once per shop by `Pages/Shop/hotProducts.js` and read
 * by every card. It is deliberately NOT folded into the product card payload:
 * the shop loads its cards a window at a time and draws them from four
 * surfaces, all of which would then have to learn a per-card flag.
 *
 * `recordProductOpen` is a beacon. The visitor key is the storefront's existing
 * `anonymousId` — the same id page views and campaign clicks already carry —
 * and it is the ONLY thing about the shopper that travels: no name, no account,
 * no address, and the server stores the day rather than the time.
 */
export async function fetchHotProducts(salonSlug) {
  if (!salonSlug) return { enabled: false, windowDays: 0, productIds: [] };
  const { data } = await API.get(`/public/salons/${salonSlug}/hot-products`);
  return data;
}

export function recordProductOpen(salonSlug, productId) {
  if (!salonSlug || !productId) return Promise.resolve();
  return API.post(
    `/public/salons/${salonSlug}/products/${productId}/open`,
    { visitorKey: getVisitorKey() },
  ).catch(() => {});
}

export function recordCollectionEvent({ salonId, collectionId, kind, productId, source }) {
  if (!salonId || !collectionId || !kind) return;
  setTimeout(() => {
    API.post(`/salons/${salonId}/product-collections/${collectionId}/events`, {
      kind,
      productId,
      source,
      visitorKey: getVisitorKey(),
    }).catch(() => {});
  }, 0);
}

/**
 * One collection and its products, by slug. Served even when the collection
 * is hidden — a shared link must keep working — so `null` here means the
 * slug genuinely doesn't exist (or the request failed), never "hidden".
 */
export async function fetchCollection(salonSlug, collectionSlug) {
  if (!salonSlug || !collectionSlug) return null;
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/collections/${collectionSlug}`);
    if (!data) return null;
    // Same serialized product-card shape `fetchPublicProducts` returns, so it
    // gets the same client-side normalization — a product must look
    // identical whether it's reached from the shop grid or a collection.
    const products = Array.isArray(data.products) ? data.products.map(applyNameAsTypeAxis) : [];
    return { ...data, products };
  } catch (_) {
    return null;
  }
}

/**
 * WHICH PRODUCTS ARE IN A COLLECTION — its members' ids, in the page's order —
 * for the shop's collection FILTER, which needs nothing else about them.
 *
 * It used to call `fetchCollection` and keep `products[].id`: the collection
 * page answer, every member as a whole card. `?fields=ids` is the same answer
 * with `productIds` in place of the cards. A server that predates it ignores the
 * parameter and sends the page, so the ids are read off its cards instead.
 *
 * `null` when it cannot tell (a failure, a 404, no slug), so a caller can tell
 * that from an empty collection. (The shop's filter narrows to nothing either
 * way, as it did when the page answer could not be fetched.)
 */
export async function fetchCollectionProductIds(salonSlug, collectionSlug) {
  if (!salonSlug || !collectionSlug) return null;
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/collections/${collectionSlug}`, {
      params: { fields: 'ids' },
    });
    if (Array.isArray(data?.productIds)) return data.productIds;
    if (Array.isArray(data?.products)) return data.products.map((p) => p?.id).filter(Boolean);
    return null;
  } catch (_) {
    return null;
  }
}

/**
 * ── THE BRAND AND COLLECTION PAGES READ THEIR MEMBERS AS ROWS ─────────────────
 *
 * dk-store's Meta collection ads land on its brand and collection pages, and
 * both pages were handed every member as a whole card (`fetchProductBrand`,
 * `fetchCollection`): 215 cards for DANIEL KLEIN, 103 for "1st August New", on
 * every visit, for a phone that draws 24. They now ask the page for
 * `?fields=index` — the page's own fields and its members as the catalogue's
 * index rows, in the page's order — and ask for cards a window at a time
 * (`fetchPublicProductWindow`, through `useCardWindow`).
 *
 * Both helpers answer `{ page, rows, cards }`:
 *
 *   `page`  the brand, or the collection, as the page answer carries it.
 *
 *   `rows`  the members, in the page's order, made the shape of the CARDS the
 *           page used to hold, for everything it decides with them. Two things
 *           make an index row differ from a card there, and both are undone:
 *             · the brand: an index row names it by id, name and slug; the card
 *               carried it whole (logo, translations, importer flag), which the
 *               brand chips and the collection's brand strip draw. The brand
 *               page's rows wear the page's own brand; a collection's rows wear
 *               the entry of its `brands` with the same id.
 *             · `createdAt`, `showInFeatured` and `tags`, which the index adds
 *               for the shop's rails and a card never had. Left on, "Newest
 *               first" would start ordering these pages by date — on a card it
 *               finds no date, so it keeps the page's own order, and that is
 *               what these pages have always shown. Changing that is a product
 *               decision, not a side effect of loading fewer bytes.
 *
 *   `cards` cards already in hand. None, from an API that answered the
 *           question. From one that predates it — and so answered the page, cards
 *           and all — every member, and those cards are the rows as well, exactly
 *           as before.
 *
 * `null` is the server saying there is no such page (404: unknown, a hidden
 * brand, an archived collection, the shop switched off). Every other failure
 * THROWS: "this brand does not exist" and "the request did not arrive" are
 * different sentences, and the page says the second with a way to try again.
 */
const ONLY_THE_INDEX_HAS = ['createdAt', 'showInFeatured', 'tags'];

function asTheCardsWere(row, wholeBrand) {
  const card = { ...row };
  for (const field of ONLY_THE_INDEX_HAS) delete card[field];
  if (card.brand?.id && wholeBrand(card.brand.id)) card.brand = wholeBrand(card.brand.id);
  return applyNameAsTypeAxis(card);
}

async function fetchLandingIndex(path) {
  try {
    const { data } = await API.get(path, { params: { fields: 'index' } });
    return data ?? null;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** A brand's page with its products as rows — see above. */
export async function fetchProductBrandIndex(salonSlug, brandIdOrSlug) {
  if (!salonSlug || !brandIdOrSlug) return null;
  const data = await fetchLandingIndex(`/public/salons/${salonSlug}/product-brands/${brandIdOrSlug}`);
  if (!data) return null;
  const { productIndex, products, ...page } = data;
  if (Array.isArray(productIndex)) {
    // The page's brand is every row's brand — as the card carried it, which has no count.
    const { productCount, ...brand } = page;
    const wholeBrand = (id) => (id === brand.id ? brand : null);
    return { page, rows: productIndex.map((row) => asTheCardsWere(row, wholeBrand)), cards: [] };
  }
  const cards = Array.isArray(products) ? products.map(applyNameAsTypeAxis) : [];
  return { page, rows: cards, cards };
}

/** A collection's page with its members as rows — see above. */
export async function fetchCollectionIndex(salonSlug, collectionSlug) {
  if (!salonSlug || !collectionSlug) return null;
  const data = await fetchLandingIndex(`/public/salons/${salonSlug}/collections/${collectionSlug}`);
  if (!data) return null;
  const { productIndex, brands, ...page } = data;
  if (Array.isArray(productIndex)) {
    const byId = new Map((Array.isArray(brands) ? brands : []).filter((b) => b?.id).map((b) => [b.id, b]));
    return { page, rows: productIndex.map((row) => asTheCardsWere(row, (id) => byId.get(id) ?? null)), cards: [] };
  }
  // An older API's page answer, cards and all: `page` keeps them, for its cover's fallback.
  const cards = Array.isArray(page.products) ? page.products.map(applyNameAsTypeAxis) : [];
  return { page: { ...page, products: cards }, rows: cards, cards };
}

// ─── Product categories (the shelf a shopper browses by) ───────────────────

/**
 * The business's visible shelves, ordered, each with the ADDRESS it lives at.
 *
 * The shop used to scrape this list off whatever products it had downloaded,
 * which was two separate wrongs. It made the shelf list a function of the
 * CURRENT FILTER — pick a brand and the pill row silently loses every shelf
 * that brand does not stock — and, now that the catalogue arrives a window at
 * a time, it would make `/shop/c/shavny-gbrym` unresolvable until the page
 * holding that shelf's products happened to load.
 *
 * The rows carry `slug`, generated by the same `slugFromName` map brands and
 * collections use, which is why the address is `shavny-gbrym` and not the
 * `שעוני-גברים` this storefront used to derive. See `utils/categorySlug`.
 *
 * Answers `[]` on any failure, and the server answers `[]` (not 404) when the
 * shop is switched off: a shelf list is not worth failing a page over.
 */
export async function fetchProductCategories(salonSlug) {
  if (!salonSlug) return [];
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/product-categories`);
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

// ─── Product brands (brand pages, the strip, the drawer) ────────────────────

/**
 * The business's visible brands, ordered.
 *
 * `officialImporter: true` narrows to the brands it is the official importer
 * of — the server's own filter rather than a client-side pass, so the main
 * page's "official importer of X" and the portal's list can never disagree.
 * Hidden brands are absent from the response entirely; nothing here filters.
 *
 * Answers `[]` on any failure: a shop whose brand endpoint is unreachable
 * must still render its products.
 */
export async function fetchProductBrands(salonSlug, { officialImporter, includeStockless } = {}) {
  if (!salonSlug) return [];
  // `includeStockless` is the יבואן רשמי marquee's question — every brand the
  // shop officially imports, including ones with nothing in the catalogue. The
  // server honours it only together with `officialImporter`, so it is only
  // ever sent together with it.
  let params;
  if (officialImporter) {
    params = includeStockless ? { officialImporter: true, includeStockless: true } : { officialImporter: true };
  }
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/product-brands`, { params });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/**
 * One brand and its products, by SLUG (an id is accepted as a compatibility
 * arm — see `utils/productBrands.brandKey`). A hidden brand 404s, so `null`
 * here means "no such brand, or it is hidden", which is the same page either
 * way.
 *
 * The products get `applyNameAsTypeAxis` exactly as the shop grid's and the
 * collection page's do, so a product looks identical wherever it is reached
 * from.
 */
export async function fetchProductBrand(salonSlug, brandIdOrSlug) {
  if (!salonSlug || !brandIdOrSlug) return null;
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/product-brands/${brandIdOrSlug}`);
    if (!data) return null;
    const products = Array.isArray(data.products) ? data.products.map(applyNameAsTypeAxis) : [];
    return { ...data, products };
  } catch (_) {
    return null;
  }
}

// ─── The top announcement bar + the features section ────────────────────────

/**
 * `{ enabled, messages[] }`. `enabled` ALREADY means bar-on AND shop-on AND at
 * least one visible message with content — the client must not re-derive any
 * of the three. The failure shape is the disabled shape, so an unreachable
 * endpoint is a page with no bar rather than a page with an empty one.
 */
export async function fetchShopAnnouncements(salonSlug) {
  if (!salonSlug) return { enabled: false, messages: [] };
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/shop-announcements`);
    return data && typeof data === 'object' ? data : { enabled: false, messages: [] };
  } catch (_) {
    return { enabled: false, messages: [] };
  }
}

/**
 * `{ enabled, items[] }` — the business-features claims, six derived from what
 * the business declares and seven manual, in ONE list. Off by default: the
 * disabled shape is also the failure shape, so the section cannot half-appear.
 */
export async function fetchShopFeatures(salonSlug) {
  if (!salonSlug) return { enabled: false, items: [] };
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/shop-features`);
    return data && typeof data === 'object' ? data : { enabled: false, items: [] };
  } catch (_) {
    return { enabled: false, items: [] };
  }
}

// ─── Recommendations ─────────────────────────────────────────────────────────

export async function getRelatedProducts(salonSlug, productId, { limit = 8, offset = 0 } = {}) {
  if (!salonSlug || !productId) return [];
  try {
    const { data } = await API.get(`/public/salons/${salonSlug}/products/${productId}/related`, {
      params: { limit, offset },
    });
    return Array.isArray(data) ? data.map(applyNameAsTypeAxis) : [];
  } catch (_) {
    return [];
  }
}

export async function getFrequentlyBoughtWith(salonSlug, variantIds) {
  if (!salonSlug || !variantIds?.length) return [];
  try {
    const { data } = await API.post(
      `/public/salons/${salonSlug}/recommendations/frequently-bought`,
      { variantIds },
    );
    return Array.isArray(data) ? data.map(applyNameAsTypeAxis) : [];
  } catch (_) {
    return [];
  }
}

// ─── Order payment link + cancel ──────────────────────────────────────────

export async function createOrderPaymentLink(salonSlug, orderId, kind = 'full') {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/orders/${orderId}/payment-link`,
    { kind },
  );
  return data; // { paymentId, amount, currency, pdescTag, description }
}

/**
 * Charge an order's outstanding amount against one of the logged-in
 * customer's saved cards (one-tap, no Tranzila iframe). Mirrors the
 * subscription + pending-extra saved-card flow: the server resolves the
 * SavedPaymentMethod by id (scoped to the salon + the authenticated
 * customer) and charges its token on the token terminal.
 *
 * Requires the customer JWT — an anonymous/guest checkout has no saved
 * cards. Two-step by design: create the order first (createOrder), then
 * call this against the returned order id.
 *
 * On a Cal-style decline the server responds 400 `errorCode=CVV_REQUIRED`;
 * the caller catches, prompts for the 3-digit CVV, and replays this call
 * with `cvv` set. The server charges the OUTSTANDING amount (not the raw
 * total), so a retry / double-tap is idempotent and never double-charges.
 *
 * Returns { paymentId, status: 'CAPTURED', idempotent, paymentStatus }
 * where paymentStatus is the resulting Order.paymentStatus ('PAID' |
 * 'PARTIAL'; may be undefined on the idempotent early-return path).
 */
export async function chargeOrderWithSavedCard(salonSlug, orderId, savedPaymentMethodId, cvv) {
  const body = { savedPaymentMethodId };
  if (cvv) body.cvv = cvv;
  const { data } = await API.post(
    `/public/salons/${salonSlug}/orders/${orderId}/charge-saved-card`,
    body,
  );
  return data;
}

// ─── A checkout attempt's payment (2026-10-17) ────────────────────────────
/* THE ORDER IS WHAT MONEY PRODUCES. A card checkout (or a cash one whose deposit is taken by card) is answered by
 * `createOrder` with `{ checkoutAttemptId, awaitingPayment, total, currency, payBy }` and no order: these are the
 * twins of the order's payment calls above, pointed at `/public/salons/:slug/checkouts/:attemptId/…`. Every one a
 * guest may call carries the visitor key the attempt was opened with — the attempt id alone opens nothing; the
 * saved card is the buyer's bearer only. A paid attempt is refused `ATTEMPT_ALREADY_PAID` with its `orderId`. */

/** The card session for the attempt: `{ paymentId, amount, currency, pdescTag, notifyNonce, description }`. */
export async function createCheckoutPaymentLink(salonSlug, attemptId, kind) {
  const body = { visitorKey: getVisitorKey() };
  if (kind) body.kind = kind;
  const { data } = await API.post(`/public/salons/${salonSlug}/checkouts/${attemptId}/payment-link`, body);
  return data;
}

/** The wallet handshake for the attempt: `{ paymentId, thtk, terminal, amount, currency, tranMode, googlePay }`. */
export async function getCheckoutHostedFieldsHandshake(salonSlug, attemptId, kind) {
  const body = { visitorKey: getVisitorKey() };
  if (kind) body.kind = kind;
  const { data } = await API.post(`/public/salons/${salonSlug}/checkouts/${attemptId}/hosted-fields-handshake`, body);
  return data;
}

/**
 * The wallet sheet's charge, relayed. `paymentId` is the handshake's and is REQUIRED (the charge is bound to that
 * row); `payload` is `{ transactionResponse, paymentMethod }`. Answers `{ paymentId, orderId }`, or `orderId: null`
 * with `pending: 'CONFIRMING' | 'PLACING'` while the money is landed but the order not yet written.
 */
export async function completeCheckoutHostedFieldsCharge(salonSlug, attemptId, paymentId, payload) {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/checkouts/${attemptId}/complete-hosted-fields-charge`,
    { ...(payload || {}), paymentId, visitorKey: getVisitorKey() },
  );
  return data;
}

/** One tap with the signed-in buyer's saved card (bearer only). Answers `{ paymentId, status, orderId }`. */
export async function chargeCheckoutWithSavedCard(salonSlug, attemptId, savedPaymentMethodId, cvv) {
  const body = { savedPaymentMethodId };
  if (cvv) body.cvv = cvv;
  const { data } = await API.post(`/public/salons/${salonSlug}/checkouts/${attemptId}/charge-saved-card`, body);
  return data;
}

/** Where the attempt's money stands: `{ attemptId, paid, orderId, pending, failure, payBy, total, currency }`. */
export async function getCheckoutState(salonSlug, attemptId) {
  if (!salonSlug || !attemptId) return null;
  const { data } = await API.get(`/public/salons/${salonSlug}/checkouts/${attemptId}/state`, {
    params: { visitorKey: getVisitorKey() },
  });
  return data;
}

/** The shopper tapped Pay: an attempt that lapses unpaid from now on is somebody the shop hears about. */
export async function engageCheckout(salonSlug, attemptId) {
  const { data } = await API.post(`/public/salons/${salonSlug}/checkouts/${attemptId}/engaged`, { visitorKey: getVisitorKey() });
  return data;
}

export async function cancelMyOrder(orderId, reason) {
  const { data } = await API.post(`/me/orders/${orderId}/cancel`, { reason });
  return data;
}

// ─── My stays (customer, hospitality) ────────────────────────────────────────
// The booking analog of getMyOrders / getMyOrder / cancelMyOrder above. Same
// axios instance (Bearer-token auth), same base, same error surfacing — just
// re-pointed at the authenticated `/me/stays` routes. The server resolves
// ownership from the caller's phone/customer identity, so a customer only ever
// sees their own bookings.

/**
 * The logged-in customer's stay bookings, newest first, ALL statuses (compact
 * card shape). The server wraps the list as `{ stays: [...] }`, so unwrap it
 * here — callers get a plain array (mirrors getMyOrders returning `[]` on a
 * miss so a strip/list never crashes on a logged-out / errored fetch).
 */
export async function getMyStays() {
  try {
    const { data } = await API.get('/me/stays');
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.stays) ? data.stays : [];
  } catch (_) {
    return [];
  }
}

/** Full details for one of the caller's stays (404 unknown / 403 not theirs). */
export async function getMyStay(stayId) {
  if (!stayId) return null;
  const { data } = await API.get(`/me/stays/${stayId}`);
  return data;
}

/**
 * Caller cancels their OWN booking — allowed only when status is REQUEST or
 * CONFIRMED, else the server 400s with `{ code: 'BOOKING_NOT_CANCELLABLE' }`.
 * Mirrors cancelMyOrder (reason in the body). Never auto-refunds.
 */
export async function cancelMyStay(stayId, reason) {
  const { data } = await API.patch(`/me/stays/${stayId}/cancel`, { reason });
  return data;
}

/**
 * Mint a Tranzila hosted-fields handshake (`thtk`) for the order's
 * pending payment-link amount. Required before chargeGpay / hfChargeApple.
 * The order must have a PENDING Payment row of the requested kind
 * (created via createOrderPaymentLink) — the handshake is anchored to
 * that row so the FE can't ask for a different amount than the server
 * committed to.
 *
 * Returns { paymentId, thtk, terminal, amount, currency, tranMode }.
 */
export async function getOrderHostedFieldsHandshake(salonSlug, orderId, kind = 'full') {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/orders/${orderId}/hosted-fields-handshake`,
    { kind },
  );
  return data;
}

/**
 * Persist a successful Tranzila hosted-fields AP/GP charge against the
 * order. Idempotent on transaction_id — replaying a successful call
 * returns the same paymentId. Mirrors the booking-side
 * completeHostedFieldsCharge for orders.
 *
 * Returns { paymentId, idempotent, paymentStatus? }.
 */
export async function completeOrderHostedFieldsCharge(salonSlug, orderId, transactionResponse, paymentMethod, paymentId) {
  // `paymentId` is the PENDING row the handshake was minted for. Sent so the
  // server completes against THAT row — not "the newest pending row of any
  // kind", which charged a deposit and then refused it against a full-payment
  // row minted in another tab (PAY-11). Omitted when unknown; the server keeps
  // its fallback.
  const body = { transactionResponse, paymentMethod };
  if (paymentId) body.paymentId = paymentId;
  const { data } = await API.post(
    `/public/salons/${salonSlug}/orders/${orderId}/complete-hosted-fields-charge`,
    body,
  );
  return data;
}

// ─── Hospitality: stay (booking) card payment ────────────────────────────────
// Booking-keyed analog of the order payment wrappers above. A booking, like an
// order, is a PRE-EXISTING entity the payment attaches to — so these mirror the
// order wrappers exactly (same axios instance, same base, same error surfacing),
// re-pointed at the `.../stays/:bookingId/...` routes. The routing tag the server
// bakes into the iframe pdesc is `b:<bookingId>s:<salonId>` (see createPaymentLink
// on the server's BookingPaymentsService).
//
// NOTE the two shape differences vs. orders:
//   • `kind` here is UPPERCASE 'DEPOSIT' | 'FULL' (the server's BookingPaymentKind),
//     not the order flow's lowercase 'deposit' | 'full'.
//   • charge-saved-card is auth-scoped at `me/stays/...` (no salon slug) and takes
//     the SavedPaymentMethod id in the body, not the path.

/**
 * Pay-sheet info for a booking — the amount to show BEFORE minting a payment.
 * Public (no auth). Returns:
 *   { dueRemaining, depositAmount, depositDue, currency, guestName,
 *     propertyName, acceptCardDeposit, acceptsCardPayments }
 *
 * The server gates this behind the SAME card-deposit rule as the charge flow, so
 * a property that doesn't accept a card deposit (or a business that doesn't take
 * cards) responds 400 `CARD_DEPOSIT_NOT_ENABLED` rather than returning the flags
 * false — the caller treats any 4xx/5xx here as "nothing to pay / contact host".
 */
export async function getStayPayInfo(salonSlug, bookingId) {
  const { data } = await API.get(
    `/public/salons/${salonSlug}/stays/${bookingId}/pay-info`,
  );
  return data;
}

/**
 * Mint a Tranzila iframe payment session for a booking's deposit or balance.
 * `kind='DEPOSIT'` charges the configured booking deposit (capped at what's due);
 * `kind='FULL'` charges the whole outstanding balance. Mirror of
 * createOrderPaymentLink. Returns { paymentId, amount, currency, pdescTag,
 * description } where pdescTag is `b:<bookingId>s:<salonId>`.
 */
export async function createStayPaymentLink(salonSlug, bookingId, kind = 'DEPOSIT') {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/stays/${bookingId}/payment-link`,
    { kind: kind === 'FULL' ? 'FULL' : 'DEPOSIT' },
  );
  return data; // { paymentId, amount, currency, pdescTag, description }
}

/**
 * Mint a Tranzila hosted-fields handshake (`thtk`) for the booking's pending
 * payment-link amount. Required before chargeGpay / hfChargeApple. Mirror of
 * getOrderHostedFieldsHandshake — a PENDING payment-link Payment of the same
 * `kind` must already exist (call createStayPaymentLink first). Returns
 * { paymentId, thtk, terminal, amount, currency, tranMode, googlePay }.
 *
 * `provider` here is only the FE's DEPOSIT/FULL selector — Apple vs Google is
 * decided at charge time by which button the customer taps (the handshake bundle
 * already carries both the AP terminal and the GP token terminal in `googlePay`).
 */
export async function getStayHostedFieldsHandshake(salonSlug, bookingId, provider = 'DEPOSIT') {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/stays/${bookingId}/hosted-fields-handshake`,
    { kind: provider === 'FULL' ? 'FULL' : 'DEPOSIT' },
  );
  return data;
}

/**
 * Persist a successful Tranzila hosted-fields AP/GP charge against the booking.
 * Idempotent on the gateway transaction id. Mirror of
 * completeOrderHostedFieldsCharge. `body` is { transactionResponse, paymentMethod }
 * — the raw Tranzila `transaction_response` object + 'apple_pay' | 'google_pay'.
 * Returns { paymentId, idempotent, leg?, amount? }.
 */
export async function completeStayHostedFieldsCharge(salonSlug, bookingId, body) {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/stays/${bookingId}/complete-hosted-fields-charge`,
    body,
  );
  return data;
}

/**
 * Charge a booking's deposit/balance against one of the logged-in customer's
 * saved cards (one-tap, no Tranzila iframe). Auth-scoped at `me/stays/...` — an
 * anonymous/guest has no saved cards. Mirror of chargeOrderWithSavedCard but the
 * booking id is in the PATH and the SavedPaymentMethod id is in the BODY.
 *
 * `body` = { savedPaymentMethodId, kind?, cvv? }. On a Cal-style decline the
 * server responds 400 `errorCode=CVV_REQUIRED`; the caller catches, prompts for
 * the 3-digit CVV, and replays with `cvv` set. The server charges the OUTSTANDING
 * amount (never the raw total) so a retry/double-tap is idempotent and never
 * double-charges. Returns { paymentId, status: 'CAPTURED', idempotent }.
 */
export async function chargeStayWithSavedCard(bookingId, body) {
  const { data } = await API.post(
    `/me/stays/${bookingId}/charge-saved-card`,
    body,
  );
  return data;
}

/**
 * Guest-facing "my request" status for a booking — the manual-deposit flow's
 * status panel reads this after the guest submits a request (`?request=<id>`).
 * Public (no auth). Returns:
 *   { status, propertyName, guestName, checkInDate, checkOutDate, nights,
 *     total, depositAmount, depositStatus, depositDue, dueRemaining, currency,
 *     acceptCardDeposit, acceptsCardPayments,
 *     depositInstructions: { message, bankDetails, bitPhone },
 *     depositEvidenceUrls: string[] }
 *
 * `locale` steers the localized deposit-instructions `message` (the business
 * writes per-language copy). Caller treats any 4xx/5xx (booking not found /
 * property gone) as "request not found" and shows a friendly fallback.
 */
export async function getStayRequestStatus(salonSlug, bookingId, locale) {
  const params = {};
  if (locale && ['en', 'ar', 'he'].includes(locale)) params.locale = locale;
  const { data } = await API.get(
    `/public/salons/${salonSlug}/bookings/${bookingId}/request-status`,
    { params },
  );
  return data;
}

/**
 * Upload a manual-deposit proof (bank-transfer receipt / Bit screenshot) for a
 * booking — image or PDF. Public multipart (mirrors the review/customer-photo
 * upload shape: a single `file` field). Returns the refreshed
 * `{ depositEvidenceUrls }`; the caller re-fetches the status to reflect it.
 */
export async function uploadStayEvidence(salonSlug, bookingId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await API.post(
    `/public/salons/${salonSlug}/bookings/${bookingId}/evidence`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data; // { depositEvidenceUrls }
}

// ─── Hospitality: public property storefront ─────────────────────────────────
export async function getPublicProperties(salonSlug) {
  const { data } = await API.get(`/public/salons/${salonSlug}/properties`);
  return data;
}

export async function getPublicProperty(salonSlug, propertySlug) {
  const { data } = await API.get(`/public/salons/${salonSlug}/properties/${propertySlug}`);
  return data;
}

// ─── Courses: public course storefront (plan 7, F-200/201/202) ───────────────
// Unauthenticated — matches the products/properties precedent above. The
// server's PublicCoursesController is the one route in the courses module
// with no auth guard at all; scoping is entirely server-side (see
// stylon-server/src/modules/public/public-courses.service.ts).
export async function getPublicCourses(salonSlug) {
  const { data } = await API.get(`/public/salons/${salonSlug}/courses`);
  return data;
}

export async function getPublicCourse(salonSlug, courseSlug) {
  const { data } = await API.get(`/public/salons/${salonSlug}/courses/${courseSlug}`);
  return data;
}

/**
 * Register the SIGNED-IN customer for a course (the seam plan 7 left open).
 *
 * Keyed by salon ID, not the slug the two GETs above use, and that difference
 * is load-bearing: the server gates this route with `CapabilityGuard`, which
 * resolves the business from a `:salonId` path param and 404s when it cannot —
 * a slug-keyed path would 404 forever. The id is on the course facts payload
 * (`course.salonId`), and unlike the slug it is also present in custom-domain
 * mode, where the URL carries no slug at all.
 *
 * Identity is NOT in the body. The server reads it from the bearer token that
 * `Config/Api`'s request interceptor attaches, exactly as `createAppointment`
 * does — the caller's job is to make sure there IS a session first (open
 * `LoginBookModal` if not), never to describe who they are here.
 *
 * The body is rebuilt from `runId` alone rather than spread: the server's
 * global ValidationPipe runs `forbidNonWhitelisted`, so one stray property is
 * a 400 rather than a silent ignore.
 */
/**
 * The page's own language, off the key i18next's detector is configured to
 * read and write (`lookupLocalStorage: 'language'` in src/i18n.js).
 *
 * Read from storage rather than by importing the i18n instance: importing it
 * boots i18next, and `storeApi` is imported by very nearly every component —
 * which made nine unrelated test suites fail on a language detector that has no
 * browser to detect in. The key is the same one the language picker writes, so
 * this is the same answer by a cheaper route.
 */
function readPageLanguage() {
  try {
    const raw = (typeof localStorage !== 'undefined' && localStorage.getItem('language')) || '';
    const lang = String(raw).split('-')[0].toLowerCase();
    return lang === 'ar' || lang === 'he' ? lang : 'en';
  } catch (_) {
    return 'en';
  }
}

export async function enrolInCourse(salonId, courseId, { runId, trackId, sessionIds, email, marketingConsent, answers, updateProfile } = {}) {
  const body = runId ? { runId } : {};
  // Omitted, never sent empty: the server refuses a trackId on a course that
  // has no tracks (design §1.1 — a concept a shape lacks must not be shown,
  // and must not be silently accepted either), and `forbidNonWhitelisted`
  // turns anything unexpected into a 400.
  if (trackId) body.trackId = trackId;
  // WHICH MEETINGS, for a PER_LESSON or QUOTA course. Omitted when empty for
  // exactly the reason `trackId` is: the server REFUSES a meeting list on a
  // shape that has no chooser (`MEETINGS_NOT_APPLICABLE`).
  //
  // This function REBUILDS the body rather than spreading it, which is correct
  // — `forbidNonWhitelisted` turns one stray property into a 400 — and is also
  // why a new field has to be added HERE as well as at the call site. Driving
  // the real page is what found that: the picker held two meetings, the running
  // total said ₪500, the CTA was live, and the request went out carrying only
  // `runId`. The server refused it with MEETINGS_REQUIRED, which is the system
  // working; the page asking for something it had already been told is the
  // defect. A component test that mocks this function cannot see it, so
  // `storeApi.courseEnrol.test.js` asserts the WIRE.
  if (sessionIds && sessionIds.length) body.sessionIds = sessionIds;
  // The registration form. Each of the three is omitted rather than sent empty,
  // for the same reason `trackId` is: the server REFUSES an email on a course
  // that does not ask for one and a consent on a course that does not offer the
  // tick, because a value accepted for a question nobody put is a record of
  // nothing.
  if (email) body.email = email;
  // Exactly `true`, never a coercion. `Boolean('false') === true` has already
  // shipped one defect in this codebase, and this field is a legal record.
  if (marketingConsent === true) body.marketingConsent = true;
  if (answers && Object.keys(answers).length) body.answers = answers;
  // "Also update my profile" (SWP-I-36 b). `buildEnrolBody` sets it and this
  // list dropped it, so the tick did nothing. Exactly `true` again: the server
  // reads anything else as "keep both".
  if (updateProfile === true) body.updateProfile = true;

  const { data } = await API.post(`/public/salons/${salonId}/courses/${courseId}/enroll`, body, {
    /**
     * THE LANGUAGE THE CONSENT SENTENCE WAS READ IN.
     *
     * The server snapshots the wording onto the enrolment, and a consent record
     * has to reproduce the words the person actually saw — so the page's own
     * language is sent explicitly rather than left to `Accept-Language`, which
     * is the browser's opinion and not the reader's choice. `X-Locale` is the
     * header `getLocaleFromRequest` reads first.
     */
    headers: { 'X-Locale': readPageLanguage() },
  });
  return data;
}

/** Join the queue for a full course (F-130, customer half). Same rules as above. */
export async function joinCourseWaitlist(salonId, courseId, { runId } = {}) {
  const body = runId ? { runId } : {};
  const { data } = await API.post(`/public/salons/${salonId}/courses/${courseId}/waitlist`, body);
  return data;
}

// ─── Courses: the student's OWN course (wave 3 — self-paced, design §13.6) ────
//
// Authenticated, and keyed by ENROLMENT rather than by salon or course. That is
// the whole privacy story in one URL shape: the server resolves the caller's
// Customer from the bearer token and matches it against the enrolment named
// here, so the id in the path is checked rather than trusted, and the WHERE
// clause that reads progress is a single equality with no OR to get wrong.
//
// No `salonId` anywhere, deliberately: these routes carry no CapabilityGuard
// (a customer token has no salon for it to resolve), and the gate is enforced
// server-side against the enrolment's own business — see
// me-course-lessons.controller.ts.

/** Every course this customer has bought, with their own progress on each. */
export async function fetchMyCourses() {
  const { data } = await API.get('/me/courses');
  return Array.isArray(data) ? data : [];
}

/**
 * ONE ENROLMENT'S COURSE — the next meeting, the whole schedule, the business's
 * announcements and the balance, in a single request (design §13.6).
 *
 * Always re-fetched, never cached. A sibling feature moves sessions and notifies
 * the student that their class has changed; a notification whose link opens a
 * cached screen showing the OLD time is worse than no notification at all, so
 * this call carries no client-side memo and the page re-reads it on mount and
 * whenever the tab regains focus.
 *
 * `schedule.sessions` is `null` — not `[]` — for a shape that has no schedule
 * (design §1.1: "not greyed out, not empty-stated, *absent*"). The two are
 * different answers and the page renders them differently.
 */
export async function fetchMyCourseOverview(enrollmentId) {
  const { data } = await API.get(`/me/courses/${enrollmentId}`);
  return data;
}

/** One enrolment's lesson list — each row carries whether it is open, and if not, why. */
export async function fetchMyCourseLessons(enrollmentId) {
  const { data } = await API.get(`/me/courses/${enrollmentId}/lessons`);
  return data;
}

/** One lesson and its node tree. 403 `LESSON_LOCKED` when it has not opened yet. */
export async function fetchMyCourseLesson(enrollmentId, lessonId) {
  const { data } = await API.get(`/me/courses/${enrollmentId}/lessons/${lessonId}`);
  return data;
}

/**
 * Record progress. A bare call is "they opened it"; `{ status: 'COMPLETED' }`
 * is the student pressing Mark complete.
 *
 * The body is REBUILT rather than spread, same as `enrolInCourse` above and for
 * the same reason: the server's ValidationPipe runs `forbidNonWhitelisted`, so
 * one stray property is a 400 rather than a silent ignore. There is in
 * particular no way to send `unlocked` or `completedAt` — access is the
 * server's to compute and the completion clock is the server's to keep.
 */
export async function saveLessonProgress(enrollmentId, lessonId, { status, lastNodeId } = {}) {
  const body = {};
  if (status) body.status = status;
  if (lastNodeId) body.lastNodeId = lastNodeId;
  const { data } = await API.post(`/me/courses/${enrollmentId}/lessons/${lessonId}/progress`, body);
  return data;
}

/**
 * WHAT THIS STUDENT MAY STILL PICK BETWEEN — PER_LESSON and QUOTA only.
 *
 * A SECOND read rather than a field on the overview, mirroring the server's own
 * split: the overview answers "what is my timetable", which every shape has,
 * and this answers "what could my timetable be", which only two do. It carries
 * how many places are left in each meeting — a number a student reading their
 * schedule has no use for, and the one a student CHOOSING cannot choose
 * without.
 *
 * Refused with `MEETINGS_NOT_APPLICABLE` on every other shape, so the caller
 * gates on `schedulingMode` rather than asking and discarding.
 */
export async function fetchMyCourseMeetingChoice(enrollmentId) {
  const { data } = await API.get(`/me/courses/${enrollmentId}/meeting-choice`);
  return data;
}

/**
 * Change them. The COMPLETE set of upcoming meetings, never a delta — un-ticking
 * is how a meeting is dropped, and saving the same list twice does nothing.
 *
 * The body is REBUILT from one named field rather than spread, same as
 * `enrolInCourse` and `saveLessonProgress` above and for the same reason: the
 * server runs `forbidNonWhitelisted`, so one stray property is a 400 rather
 * than a silent ignore. That is not a hypothetical here — `sessionIds` was
 * dropped on the wire exactly this way on the enrolment path, with the picker
 * showing two meetings ticked and the request carrying only `runId`. There is
 * in particular no `enrollmentId` in the body: the enrolment is the path, and
 * the server proves it is the caller's own before it reads a single seat.
 */
export async function saveMyCourseMeetingChoice(enrollmentId, sessionIds) {
  const body = { sessionIds: Array.isArray(sessionIds) ? sessionIds : [] };
  const { data } = await API.post(`/me/courses/${enrollmentId}/meeting-choice`, body);
  return data;
}

export async function checkPropertyAvailability(salonSlug, propertySlug, params) {
  const { data } = await API.get(
    `/public/salons/${salonSlug}/properties/${propertySlug}/availability`,
    { params },
  );
  return data;
}

export async function requestPropertyBooking(salonSlug, propertySlug, body) {
  const { data } = await API.post(
    `/public/salons/${salonSlug}/properties/${propertySlug}/booking-requests`,
    body,
  );
  return data;
}

// ── Agreements the patient has to read and sign ──────────────────────────────

/**
 * What this appointment still needs signed, and what has been.
 *
 * Ownership is resolved server-side from the caller's own identity — nothing
 * identifying is sent, because a patient-facing route that accepted a customer
 * id would let anyone sign as anyone.
 */
export async function getMyAgreements(appointmentId) {
  const { data } = await API.get(`/me/appointments/${appointmentId}/agreements`);
  return Array.isArray(data?.states) ? data.states : [];
}

/** Store a signature the patient just drew, before recording the agreement. */
export async function uploadMySignature(appointmentId, blob) {
  const form = new FormData();
  form.append('file', new File([blob], 'signature.png', { type: 'image/png' }));

  const { data } = await API.post(`/me/appointments/${appointmentId}/signature-upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function signMyAgreement(appointmentId, agreementId, payload) {
  const { data } = await API.post(
    `/me/appointments/${appointmentId}/agreements/${agreementId}/sign`,
    payload,
  );
  return data;
}

/**
 * What has to be agreed to before a booking can be confirmed.
 *
 * Two routes, on purpose. The public one answers what this business REQUIRES —
 * somebody choosing a time has not signed in yet, and hiding "this clinic
 * requires a consent form" behind the login would put the requirement after
 * the step it should inform. It takes no customer id: one that did would let
 * anybody learn which consents a named person had signed here.
 *
 * The signed-in one answers what THIS person still owes, with their identity
 * taken from their token.
 */
export async function getBookingAgreements(salonId, { serviceIds = [], signedIn = false } = {}) {
  const path = signedIn
    ? `/me/salons/${salonId}/booking-agreements`
    : `/public/salons/${salonId}/booking-agreements`;

  const { data } = await API.get(path, {
    params: serviceIds.length ? { serviceIds: serviceIds.join(',') } : {},
  });

  return {
    states: Array.isArray(data?.states) ? data.states : [],
    blocking: Array.isArray(data?.blocking) ? data.blocking : [],
  };
}

// ─── Prepaid series ("paid for 8, 3 left") ───────────────────────────────────
// Two doors, and the split matters. WHAT A BUSINESS SELLS is public, because
// somebody comparing clinics has not signed in and a price behind a login is a
// price the only person it is for cannot see. WHAT I HAVE BOUGHT is read with
// the caller's own token, and the customer id is never a parameter — a
// parameter can be typed.
//
// Every read here swallows its error and returns the empty answer. A series is
// an upsell on a booking screen: a business that sells none, an offline moment
// or a 500 must all leave the booking flow exactly as it was, never block it.

/** Every live series for one service, best value first. `[]` when none. */
export async function getSeriesOffers(salonId, serviceId) {
  if (!salonId || !serviceId) return [];
  try {
    const { data } = await API.get(`/public/salons/${salonId}/series/offers/${serviceId}`);
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/**
 * The series each service row should LEAD with, keyed by service id.
 *
 * One round trip for the whole list, because a service grid asking per row is
 * how a booking screen becomes slow the day a clinic starts selling courses.
 * Returns `{}` on any failure, which reads as "no row has an offer".
 */
export async function getRecommendedSeries(salonId, serviceIds = []) {
  const ids = (Array.isArray(serviceIds) ? serviceIds : []).map(String).filter(Boolean);
  if (!salonId || ids.length === 0) return {};
  try {
    const { data } = await API.post(`/public/salons/${salonId}/series/recommended`, {
      serviceIds: ids,
    });
    return data && typeof data === 'object' ? data : {};
  } catch (_) {
    return {};
  }
}

/**
 * The caller's own series at this business — live ones first, finished last.
 *
 * `[]` for a signed-out visitor, which is why this never throws: the home page
 * and the booking flow both call it before they know whether anybody is
 * signed in.
 */
export async function getMySeries(salonId) {
  if (!salonId || !hasCustomerToken()) return [];
  try {
    const { data } = await API.get(`/my/series/${salonId}`);
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/** The caller's series that could pay for a booking of this service. */
export async function getMySpendableSeries(salonId, serviceId) {
  if (!salonId || !serviceId) return [];
  try {
    const { data } = await API.get(`/my/salons/${salonId}/series/spendable`, {
      params: { serviceId },
    });
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

/**
 * Buy a series for myself. Lands AWAITING_PAYMENT; the checkout activates it.
 *
 * This one does NOT swallow its error. Everything above is a read that may
 * come back empty; this takes money, and a failure the caller cannot see is a
 * customer who thinks they bought a course of eight.
 */
export async function purchaseSeries(salonId, seriesId, { appointmentId = null } = {}) {
  const { data } = await API.post(`/my/salons/${salonId}/series/purchase`, {
    seriesId,
    ...(appointmentId ? { appointmentId } : {}),
  });
  return data;
}

/**
 * Settle the prepaid courses a just-made booking carries.
 *
 * Does NOT swallow its error, for the same reason `purchaseSeries` does not:
 * this is money. A customer who thinks they have bought eight sessions and has
 * not needs to be told while they are still looking at the screen.
 */
export async function attachSeriesToBooking(salonId, { appointmentId, purchases = [], redemptions = [] }) {
  const { data } = await API.post(`/my/salons/${salonId}/series/attach`, {
    appointmentId,
    purchases,
    redemptions,
  });
  return data;
}
